<template>
  <paper-modal-container :is-visible="isVisible" :title="modalTitle" :hide-close-button="false" @close="cancelPopup">
    <br />

    <!-- Step Indicator -->
    <div :class="$style.stepIndicator">
      <div v-for="step in 5" :key="step"
        :class="[$style.stepItem, { [$style.active]: currentStep === step, [$style.completed]: currentStep > step }]">
        <div :class="$style.stepNumber">{{ step }}</div>
        <div :class="$style.stepLabel">{{ getStepLabel(step) }}</div>
      </div>
    </div>

    <!-- Step 1: 기본 정보 -->
    <div v-if="currentStep === 1">
      <div :class="$style.input">
        <h4 :class="$style.required">라이브 제목</h4>
        <input :class="$style.textInput" v-model="title" placeholder="라이브 제목 입력" :disabled="uploading" />
      </div>

      <div :class="$style.input">
        <h4 :class="$style.required">공연타입</h4>
        <div :class="$style.radio">
          <label>
            <input type="radio" value="vocal" v-model="type" :disabled="uploading" />
            보컬
          </label>
          <label>
            <input type="radio" value="band" v-model="type" :disabled="uploading" />
            밴드
          </label>
          <label>
            <input type="radio" value="dance" v-model="type" :disabled="uploading" />
            댄스
          </label>
          <label>
            <input type="radio" value="mix" v-model="type" :disabled="uploading" />
            혼합
          </label>
        </div>
      </div>

      <div :class="$style.input">
        <h4 :class="$style.required">공연 일시</h4>
        <VueDatePicker :class="$style.picker" v-model="selectedDateTime" locale="ko" time-picker-inline
          :format="dateFormat" :disabled="uploading" />
      </div>

      <div :class="$style.input">
        <h4>입장 일시 <span style="font-size: 12px"></span></h4>
        <VueDatePicker :class="$style.picker" v-model="entryDateTime" locale="ko" time-picker-inline
          :format="dateFormat" :disabled="uploading" placeholder="입장 시간을 선택하세요" />
      </div>

      <div :class="$style.input">
        <h4 :class="$style.required">장소</h4>
        <AutoComplete ref="autocompleteRef" v-model="place" :suggestions="filteredPlaces" @complete="searchPlaces"
          placeholder="라이브 장소 입력" :disabled="uploading" :class="[$style.autocomplete, $style.placeAutocomplete]"
          dropdown :forceSelection="false" panelClass="place-autocomplete-panel" appendTo="self" />
      </div>

      <div :class="$style.input">
        <h4 :class="$style.required">
          포스터 <span style="font-size: 12px">(권장 : 2896x4096)</span>
        </h4>
        <!-- 이미지 미리보기 -->
        <div v-if="posterPreviewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="posterPreviewUrl" alt="포스터" :class="$style.previewImage" />
          </div>
        </div>
        <label :class="$style.fileInputLabel" :for="'posterFile-' + formUid">
          <input ref="posterFileInput" :id="'posterFile-' + formUid" :class="$style.fileInputHidden" type="file"
            @change="handleFileUpload1" :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!file1">📷 파일 선택</span>
            <span v-else>📄 {{ file1.name }}</span>
          </span>
        </label>
        <div v-if="file1" :class="$style.newFileInfo">새 파일: {{ file1.name }}</div>
      </div>

      <div :class="$style.input">
        <h4>
          배너
          <span style="font-size: 12px">(등록 시 1450x485로 자동 보정)</span>
        </h4>
        <!-- 이미지 미리보기 -->
        <div v-if="bannerPreviewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="bannerPreviewUrl" alt="배너" :class="$style.previewImage" />
            <button v-if="isUpdateMode && !file2" :class="$style.deleteImageButton" @click="deleteBannerImage"
              :disabled="uploading">
              삭제
            </button>
          </div>
        </div>

        <label :class="$style.fileInputLabel" :for="'bannerFile-' + formUid">
          <input ref="bannerFileInput" :id="'bannerFile-' + formUid" :class="$style.fileInputHidden" type="file"
            @change="handleFileUpload2" :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!file2">📷 파일 선택</span>
            <span v-else>📄 {{ file2.name }}</span>
          </span>
        </label>
        <div v-if="file2" :class="$style.newFileInfo">새 파일: {{ file2.name }}</div>
      </div>
    </div>

    <!-- Step 2: 세부 이미지 -->
    <div v-if="currentStep === 2">
      <!-- Toast UI 에디터(마크다운) — 보관용. TinyMCE를 메인으로 사용 중이라 숨김 -->
      <div v-if="false" :class="$style.input">
        <h4>설명 <span style="font-size: 12px">(Toast UI · 마크다운)</span></h4>
        <div ref="descriptionEditorMount" :class="$style.descriptionEditorHost" />
      </div>

      <div :class="$style.input">
        <h4>설명</h4>
        <div :class="$style.tinyEditorHost">
          <TinyEditor v-model="descriptionHtml" :init="tinyEditorInit" :disabled="uploading" />
        </div>
      </div>

      <p :class="$style.infoText">
        여기서 등록하는 상세 이미지는 공연 상세 화면의 설명 아래에 순서대로 표시됩니다.
      </p>

      <div :class="$style.input">
        <h4>
          공연상세정보 1 <span style="font-size: 12px">(셋리스트 등)</span>
        </h4>
        <!-- 이미지 미리보기 -->
        <div v-if="detailImgPreviewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="detailImgPreviewUrl" alt="공연상세정보 1" :class="$style.previewImage" />
            <button v-if="isUpdateMode && !detailImageFile" :class="$style.deleteImageButton" @click="deleteDetailImage"
              :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
        <label :class="$style.fileInputLabel" :for="'detailImageFile-' + formUid">
          <input ref="detailImageFileInput" :id="'detailImageFile-' + formUid" :class="$style.fileInputHidden"
            type="file" @change="handleDetailImageUpload" :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!detailImageFile">📷 파일 선택</span>
            <span v-else>📄 {{ detailImageFile.name }}</span>
          </span>
        </label>
        <div v-if="detailImageFile" :class="$style.newFileInfo">새 파일: {{ detailImageFile.name }}</div>
      </div>

      <div :class="$style.input">
        <h4>공연상세정보 2 <span style="font-size: 12px"></span></h4>
        <!-- 이미지 미리보기 -->
        <div v-if="detailImg2PreviewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="detailImg2PreviewUrl" alt="공연상세정보 2" :class="$style.previewImage" />
            <button v-if="isUpdateMode && !detailImage2File" :class="$style.deleteImageButton"
              @click="deleteDetailImage2" :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
        <label :class="$style.fileInputLabel" :for="'detailImage2File-' + formUid">
          <input ref="detailImage2FileInput" :id="'detailImage2File-' + formUid" :class="$style.fileInputHidden"
            type="file" @change="handleDetailImage2Upload" :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!detailImage2File">📷 파일 선택</span>
            <span v-else>📄 {{ detailImage2File.name }}</span>
          </span>
        </label>
        <div v-if="detailImage2File" :class="$style.newFileInfo">새 파일: {{ detailImage2File.name }}</div>
      </div>

      <div :class="$style.input">
        <h4>공연상세정보 3 <span style="font-size: 12px"></span></h4>
        <div v-if="detailImg3PreviewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="detailImg3PreviewUrl" alt="공연상세정보 3" :class="$style.previewImage" />
            <button v-if="isUpdateMode && !detailImage3File" :class="$style.deleteImageButton"
              @click="deleteDetailImage3" :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
        <label :class="$style.fileInputLabel" :for="'detailImage3File-' + formUid">
          <input ref="detailImage3FileInput" :id="'detailImage3File-' + formUid" :class="$style.fileInputHidden"
            type="file" @change="handleDetailImage3Upload" :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!detailImage3File">📷 파일 선택</span>
            <span v-else>📄 {{ detailImage3File.name }}</span>
          </span>
        </label>
        <div v-if="detailImage3File" :class="$style.newFileInfo">새 파일: {{ detailImage3File.name }}</div>
      </div>

      <div :class="$style.input">
        <h4>공연상세정보 4 <span style="font-size: 12px"></span></h4>
        <div v-if="detailImg4PreviewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="detailImg4PreviewUrl" alt="공연상세정보 4" :class="$style.previewImage" />
            <button v-if="isUpdateMode && !detailImage4File" :class="$style.deleteImageButton"
              @click="deleteDetailImage4" :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
        <label :class="$style.fileInputLabel" :for="'detailImage4File-' + formUid">
          <input ref="detailImage4FileInput" :id="'detailImage4File-' + formUid" :class="$style.fileInputHidden"
            type="file" @change="handleDetailImage4Upload" :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!detailImage4File">📷 파일 선택</span>
            <span v-else>📄 {{ detailImage4File.name }}</span>
          </span>
        </label>
        <div v-if="detailImage4File" :class="$style.newFileInfo">새 파일: {{ detailImage4File.name }}</div>
      </div>
    </div>

    <!-- Step 3: 상세 정보 -->
    <div v-if="currentStep === 3">
      <div :class="$style.input">
        <h4 :class="$style.required">공연 정보 링크</h4>
        <input :class="$style.textInput" v-model="link" placeholder="https://x.com/linkook_live"
          :disabled="uploading" />
      </div>

      <div :class="$style.input">
        <h4>라인업</h4>
        <!-- 선택된 아티스트들 표시 (리스트 형태) -->
        <div v-if="selectedArtists.length > 0" :class="$style.artistList">
          <div v-for="(artist, index) in selectedArtists" :key="index" :class="$style.artistItem">
            <div :class="$style.artistInfo">
              <img v-if="artist.profileImage" :src="artist.profileImage" :alt="getArtistName(artist)"
                :class="$style.profileImage" />
              <img v-else :src="getArtistDefaultImage(artist.type)" :alt="`${getArtistName(artist)} 기본 프로필`"
                :class="$style.profileImage" />
              <div :class="$style.artistDetails">
                <div :class="$style.artistNameRow">
                  <span :class="$style.artistName">{{ getArtistName(artist) }}</span>
                  <span v-if="artist.isNew" :class="$style.newBadge">신규</span>
                </div>
                <span :class="$style.artistType">{{ formatArtistType(artist.type) }}</span>
              </div>
            </div>
            <button type="button" @click="removeArtist(index)" :class="$style.removeButton" :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
        <!-- 아티스트 추가 입력란 -->
        <div :class="$style.addArtistForm">
          <AutoComplete ref="artistAutocompleteRef" v-model="currentArtistInput" :suggestions="filteredArtists"
            @item-select="addArtistFromSuggestion" @complete="searchArtists" placeholder="아티스트명 검색 또는 신규 입력"
            :disabled="uploading" :class="$style.artistSearchAutocomplete" dropdown :forceSelection="false"
            optionLabel="label" panelClass="artist-autocomplete-panel" appendTo="self">
            <template #option="{ option }">
              <div class="artist-option">
                <img v-if="option.profileImage" :src="option.profileImage" :alt="option.name"
                  class="option-profile-image" />
                <img v-else :src="getArtistDefaultImage(option.type)" :alt="`${option.name} 기본 프로필`"
                  class="option-profile-image" />
                <div class="option-details">
                  <span class="option-name">{{ option.name }}</span>
                  <span class="option-type">{{ formatArtistType(option.type) }}</span>
                </div>
              </div>
            </template>
          </AutoComplete>
          <button type="button" :class="$style.addButton" @click="addArtistManually"
            :disabled="uploading || !(selectedArtistData || (currentArtistInput && currentArtistInput.trim()))">
            추가
          </button>
        </div>
        <!-- 기존 텍스트 입력 방식도 유지 (숨김) -->
        <input type="hidden" v-model="member" />
      </div>

      <div :class="$style.input">
        <h4>함께한 사람들 <span style="font-size: 12px">(예: 공연기획, 촬영, 스텝)</span></h4>
        <div v-if="crewSections.length > 0" :class="$style.crewDepthBlock">
          <!-- 역할별 섹션 -->
          <div :class="$style.crewSectionList">
            <div v-for="section in crewSections" :key="section.role" :class="$style.crewSection">
              <div :class="$style.crewSectionHeader">
                <h4 :class="$style.crewSectionTitle" style="margin-bottom: 0px;">{{ section.role }}</h4>
                <button type="button" :class="$style.removeRoleButton" @click="removeCrewRoleSection(section.role)"
                  :disabled="uploading">
                  역할 삭제
                </button>
              </div>
              <div v-if="section.members.length > 0" :class="$style.artistList">
                <div v-for="item in section.members" :key="`${section.role}-${item.index}`" :class="$style.artistItem">
                  <div :class="$style.artistInfo">
                    <img v-if="item.person.profileImage" :src="item.person.profileImage" :alt="getCrewName(item.person)"
                      :class="$style.profileImage" />
                    <img v-else :src="getArtistDefaultImage(item.person.type)"
                      :alt="`${getCrewName(item.person)} 기본 프로필`" :class="$style.profileImage" />
                    <div :class="$style.artistDetails">
                      <div :class="$style.artistNameRow">
                        <span :class="$style.artistName">{{ getCrewName(item.person) }}</span>
                        <span v-if="item.person.isNew" :class="$style.newBadge">신규</span>
                      </div>
                      <span :class="$style.artistType">{{ formatArtistType(item.person.type) }}</span>
                    </div>
                  </div>
                  <button type="button" @click="removeCrew(item.index)" :class="$style.removeButton"
                    :disabled="uploading">
                    삭제
                  </button>
                </div>
              </div>
              <div :class="[$style.addArtistForm, $style.crewAddForm]">
                <AutoComplete v-model="crewInputByRole[section.role]"
                  :suggestions="filteredCrewByRole[section.role] || []"
                  @item-select="addCrewFromSuggestionByRole($event, section.role)"
                  @complete="searchCrewByRole($event, section.role)" :placeholder="`아티스트 검색 또는 신규 입력`"
                  :disabled="uploading" :class="$style.artistSearchAutocomplete" dropdown :forceSelection="false"
                  optionLabel="label" panelClass="artist-autocomplete-panel" appendTo="self">
                  <template #option="{ option }">
                    <div class="artist-option">
                      <img v-if="option.profileImage" :src="option.profileImage" :alt="option.name"
                        class="option-profile-image" />
                      <img v-else :src="getArtistDefaultImage(option.type)" :alt="`${option.name} 기본 프로필`"
                        class="option-profile-image" />
                      <div class="option-details">
                        <span class="option-name">{{ option.name }}</span>
                        <span class="option-type">{{ formatArtistType(option.type) }}</span>
                      </div>
                    </div>
                  </template>
                </AutoComplete>
                <button type="button" :class="$style.addButton" @click="addCrewManuallyByRole(section.role)"
                  :disabled="uploading || !canAddCrewForRole(section.role)">
                  추가
                </button>
              </div>
            </div>
          </div>
        </div>
        <p v-else :class="$style.infoText">먼저 역할 섹션을 추가한 뒤 아티스트를 등록할 수 있습니다.</p>

        <div :class="$style.crewRoleSelector">
          <AutoComplete ref="crewRoleAutocompleteRef" v-model="currentCrewRoleInput" :suggestions="filteredCrewRoles"
            @complete="searchCrewRoles" @item-select="addCrewRoleFromSuggestion" placeholder="역할 검색 또는 신규 입력"
            :disabled="uploading" :class="$style.crewRoleAutocomplete" dropdown :forceSelection="false"
            panelClass="artist-autocomplete-panel" appendTo="self" />
          <button type="button" :class="$style.addRoleButton" @click="addCrewRoleSection"
            :disabled="uploading || !pendingCrewRole">
            역할 섹션 추가
          </button>
        </div>
      </div>

      <div :class="$style.input">
        <h4>포스터 작가명 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="posterCreatorNickname" placeholder="포스터 작업자 닉네임"
          :disabled="uploading" />
      </div>

      <div :class="$style.input">
        <h4>포스터 작가 링크 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="posterDetailLink" placeholder="https://x.com/poster_artist"
          :disabled="uploading" />
      </div>

    </div>

    <!-- Step 5: 예매 방식 -->
    <div v-if="currentStep === 5">
      <!-- <div :class="$style.stepDescription">
        <p v-if="userStore.auth === 'admin'">예매 방식을 선택해주세요. 링꾹예매 또는 외부예매 중 하나를 선택해야 최종 등록이 가능합니다.</p>
        <p v-else>예매 링크를 입력해주세요. 최종 등록을 위해 필수입니다.</p>
        <p v-if="userStore.auth === 'admin'" style="margin-top: 6px; font-size: 12px; color: #888;">링꾹예매 선택 시 입금 정보를
          입력하면 예매자에게 안내됩니다. 외부예매에서 현장예매 only 체크 시 예매 링크 없이 등록 가능합니다.</p>
      </div> -->

      <div :class="$style.input">
        <h4 :class="$style.required">예매 방식</h4>
        <div :class="$style.radio">
          <label>
            <input type="radio" value="external" v-model="reservationType" :disabled="uploading" />
            외부 예매
          </label>
          <label>
            <input type="radio" value="linkook" v-model="reservationType" :disabled="uploading" />
            링꾹 예매
          </label>
        </div>
      </div>

      <div v-if="reservationType === 'external' && !onsiteOnly" :class="$style.input">
        <h4 :class="!onsiteOnly ? $style.required : ''">
          예매 링크
          <span style="font-size: 12px">(현장예매 only 체크 시 선택사항)</span>
        </h4>
        <input :class="$style.textInput" v-model="reservationLink" placeholder="https://live.linkook.it/live/1234567890"
          :disabled="uploading" />
      </div>

      <!-- 외부예매 정보 -->
      <div v-if="reservationType === 'external' && !onsiteOnly" :class="$style.input">
        <h4>사전예매가 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="advancePrice" placeholder="20,000원" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'external'" :class="$style.input">
        <h4 :class="{ [$style.required]: onsiteOnly }">현장예매가 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="doorPrice" placeholder="25,000원" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'external'" :class="$style.input">
        <label :class="$style.checkboxLabel">
          <input type="checkbox" v-model="onsiteOnly" :disabled="uploading" :class="$style.checkbox" />
          현장예매 only (예매 링크 없음)
        </label>
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">링꾹예매가 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="linkookPrice" placeholder="20,000원" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4>현장예매가 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="doorPrice" placeholder="25,000원" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">링꾹티켓수 <span style="font-size: 12px; color: #888;"></span></h4>
        <input type="number" :class="$style.textInput" v-model.number="linkookTicketCount" placeholder="200"
          :disabled="uploading" min="0" step="1" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">인당 구매제한 <span style="font-size: 12px; color: #888;">(1~5장)</span></h4>
        <div :class="$style.quantityButtonGroup">
          <button v-for="n in 5" :key="n" type="button"
            :class="[$style.quantityBtn, { [$style.selected]: linkookPerPersonLimit === n }]"
            @click="linkookPerPersonLimit = n" :disabled="uploading">
            {{ n }}장
          </button>
        </div>
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">정산 은행 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="bankName" placeholder="카카오뱅크" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">정산 계좌번호 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="accountNumber" placeholder="3333-01-1234567" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">정산 예금주 <span style="font-size: 12px"></span></h4>
        <input :class="$style.textInput" v-model="accountHolder" placeholder="홍길동" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">주최자명</h4>
        <input :class="$style.textInput" v-model="organizerName" placeholder="판매자명 입력" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">주최자연락처</h4>
        <input :class="$style.textInput" v-model="organizerContact" placeholder="010-1234-5678" :disabled="uploading" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">예매 시작일 <span style="font-size: 12px"></span></h4>
        <VueDatePicker v-model="reservationStartDate" locale="ko" time-picker-inline :format="dateFormat"
          :disabled="uploading" placeholder="예매 시작일을 선택하세요" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <h4 :class="$style.required">예매 종료일 <span style="font-size: 12px"></span></h4>
        <VueDatePicker v-model="reservationEndDate" locale="ko" time-picker-inline :format="dateFormat"
          :disabled="uploading" placeholder="예매 종료일을 선택하세요" />
      </div>

      <div v-if="reservationType === 'linkook'" :class="$style.input">
        <p :class="$style.infoText">
          💡 예매 폼 질문 설정은 공연 등록 후 공연 상세 화면에서 가능합니다.
        </p>
      </div>

    </div>

    <!-- Step 4: 영상 등록 -->
    <div v-if="currentStep === 4">
      <div :class="$style.input">
        <h4>유튜브 영상 <span style="font-size: 12px; color: #999;">(선택)</span></h4>
        <div :class="$style.youtubeInputRow">
          <input :class="$style.textInput" v-model="youtubeInput" placeholder="YouTube URL을 붙여넣으세요"
            :disabled="uploading || youtubeAdding" @keydown.enter.prevent="addYoutubeVideo" />
          <button type="button" :class="$style.addButton" @click="addYoutubeVideo"
            :disabled="uploading || youtubeAdding || !youtubeInput.trim()">
            <span v-if="youtubeAdding">...</span>
            <span v-else>추가</span>
          </button>
        </div>
        <div v-if="youtubeVideos.length > 0" :class="$style.youtubeVideoList">
          <div v-for="(video, idx) in youtubeVideos" :key="video.vid" :class="$style.youtubeVideoItem">
            <img :src="`https://img.youtube.com/vi/${video.vid}/mqdefault.jpg`" :class="$style.youtubeVideoThumb"
              :alt="video.title" />
            <div :class="$style.youtubeVideoInfo">
              <div :class="$style.youtubeVideoTitle">{{ video.title }}</div>
              <div :class="$style.youtubeVideoChannel">{{ video.channel }}</div>
            </div>
            <button type="button" :class="$style.removeButton" @click="removeYoutubeVideo(idx)" :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 버튼 영역 -->
    <div :class="$style.buttons">
      <button :class="$style.cancel" @click="cancelPopup" :disabled="uploading">
        취소
      </button>
      <button v-if="currentStep > 1" :class="$style.prevButton" @click="prevStep" :disabled="uploading">
        이전
      </button>
      <button v-if="showSaveDraftButton" :class="$style.saveDraft" @click="handleSaveDraft"
        :disabled="uploading || !canSaveDraft">
        <span v-if="!uploading">임시저장</span>
        <span v-else> <i :class="$style.spinner"></i> {{ loadingText }}</span>
      </button>
      <button v-if="currentStep < 5" :class="$style.nextButton" @click="nextStep" :disabled="uploading">
        다음
      </button>
      <button v-if="currentStep === 5" :class="$style.register" @click="handleSubmit" :disabled="uploading">
        <span v-if="!uploading">{{ submitButtonText }}</span>
        <span v-else> <i :class="$style.spinner"></i> {{ loadingText }}</span>
      </button>
    </div>
  </paper-modal-container>

  <!-- 배너 이미지 크롭 모달 -->
  <div v-if="showCropModal"
    style="position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;">
    <div
      style="background:#fff;border-radius:12px;width:min(860px,96vw);max-height:90vh;display:flex;flex-direction:column;box-shadow:0 8px 32px rgba(0,0,0,.25);overflow:hidden;">
      <div style="padding:18px 24px 12px;">
        <h3 style="margin:0 0 4px;font-size:17px;font-weight:800;color:#212529;">이미지 크롭</h3>
        <p style="margin:0;font-size:13px;color:#868e96;">배너 비율(1450×485)에 맞게 원하는 영역을 선택하세요.</p>
      </div>
      <div
        style="flex:1;min-height:0;overflow:auto;background:#1a1a1a;display:flex;align-items:center;justify-content:center;padding:8px;">
        <img ref="cropImage" :src="cropSrc" alt="크롭 원본" style="display:block;max-width:100%;" />
      </div>
      <div style="display:flex;justify-content:flex-end;gap:10px;padding:14px 24px;border-top:1px solid #e9ecef;">
        <button type="button"
          style="padding:10px 22px;border-radius:8px;border:1px solid #dee2e6;background:#fff;color:#495057;font-size:14px;cursor:pointer;"
          @click="cancelCrop">취소</button>
        <button type="button"
          style="padding:10px 22px;border-radius:8px;border:none;background:#0d6efd;color:#fff;font-size:14px;cursor:pointer;"
          @click="confirmCrop">적용</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { createLive, updateLive, saveNewArtists } from "@/utils/write/live";
import axios from "axios";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { createToast } from "mosha-vue-toastify";
import { useUserStore } from "@/store/user";
import "mosha-vue-toastify/dist/style.css";
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import AutoComplete from "primevue/autocomplete";
import { nextTick } from "vue";
import Cropper from "cropperjs/dist/cropper.common.js";
import "cropperjs/dist/cropper.css";
import Editor from "@toast-ui/editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import "@toast-ui/editor/dist/i18n/ko-kr";
import colorSyntax from "@toast-ui/editor-plugin-color-syntax";
import "tui-color-picker/dist/tui-color-picker.css";
import "@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css";
import tableMergedCell from "@toast-ui/editor-plugin-table-merged-cell";
import "@toast-ui/editor-plugin-table-merged-cell/dist/toastui-editor-plugin-table-merged-cell.css";
import { uploadToastImage, prepareToastViewerMarkdown } from "@/utils/write/toastui";
// TinyMCE (self-hosted v6) — Toast UI와 비교용 추가 에디터
import TinyEditor from "@tinymce/tinymce-vue";
import "tinymce/tinymce";
import "tinymce/models/dom/model";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/plugins/lists";
import "tinymce/plugins/media";
import "tinymce/plugins/code";
import "tinymce/plugins/autolink";
import "tinymce-i18n/langs6/ko_KR";
import {
  getArtistTypeDisplayName,
  getArtistTypeIconKey,
  CREW_ROLE_OPTIONS,
} from "@/utils/artistTypes";

export default {
  name: "LinkookLiveForm",
  components: {
    VueDatePicker,
    PaperModalContainer,
    AutoComplete,
    TinyEditor,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create", // "create" 또는 "update"
      validator: (value) => ["create", "update"].includes(value),
    },
    liveData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      userStore: useUserStore(),
      formUid: `live-form-${Math.random().toString(36).slice(2, 10)}`,
      maxTicketCount: 200,
      currentStep: 1, // 현재 단계 (1-4)
      dateFormat: "yyyy-MM-dd HH:mm",
      selectedDateTime: new Date(),
      entryDateTime: null,
      title: "",
      place: "",
      type: "",
      link: "",
      reservationLink: "",
      organizer: "",
      reservationType: "", // 'linkook' 또는 'external'
      formActivated: false, // LiveForm 창이 활성화(표시·준비 완료)된 상태인지
      onsiteOnly: false, // 현장예매 only (외부 예매에서만 사용)
      linkookReservation: false,
      linkookPrice: "",
      linkookTicketCount: "",
      linkookPerPersonLimit: 2,
      bankName: "",
      accountNumber: "",
      accountHolder: "",
      reservationStartDate: null,
      reservationEndDate: null,
      advancePrice: "",
      doorPrice: "",
      member: "",
      posterCreatorNickname: "",
      posterDetailLink: "",
      organizerName: "",
      organizerContact: "",
      uploading: false,
      file1: null,
      file2: null,
      detailImageFile: null,
      detailImage2File: null,
      detailImage3File: null,
      detailImage4File: null,
      // 기존 이미지 파일명 (수정 모드용)
      originalImg: null,
      originalBanner: null,
      originalDetailImg: null,
      originalDetailImg2: null,
      originalDetailImg3: null,
      originalDetailImg4: null,
      // 이미지 미리보기 URL
      posterPreviewUrl: null,
      bannerPreviewUrl: null,
      detailImgPreviewUrl: null,
      detailImg2PreviewUrl: null,
      detailImg3PreviewUrl: null,
      detailImg4PreviewUrl: null,
      // 크롭 관련
      showCropModal: false,
      cropSrc: null,
      cropFile: null,
      cropper: null,
      // 이미지 삭제 플래그 (포스터는 삭제 불가)
      deletedBanner: false,
      deletedDetailImg: false,
      deletedDetailImg2: false,
      deletedDetailImg3: false,
      deletedDetailImg4: false,
      // Autocomplete 관련 데이터
      allPlaces: [],
      filteredPlaces: [],
      autocompleteRef: null,
      // 아티스트 관련 데이터
      allArtists: [],
      filteredArtists: [],
      selectedArtists: [],
      currentArtistInput: "",
      selectedArtistData: null, // 현재 선택된 아티스트 정보
      // 함께한 사람들(crew) 관련 데이터
      filteredCrew: [],
      selectedCrew: [],
      currentCrewInput: "",
      crewInputByRole: {},
      crewSelectedDataByRole: {},
      filteredCrewByRole: {},
      currentCrewRoleInput: "",
      filteredCrewRoles: [],
      selectedCrewData: null,
      crewRoleSections: [],
      activeCrewRole: "",
      crewRoleOptions: [...CREW_ROLE_OPTIONS],
      artistImageLoadPromises: {},
      artistAutocompleteRef: null,
      crewAutocompleteRef: null,
      status: "published", // 'draft' (임시저장) 또는 'published' (발행)
      // 유튜브 영상
      youtubeVideos: [],
      youtubeInput: "",
      youtubeAdding: false,
      description: "",
      descriptionHtml: "",
    };
  },
  computed: {
    isUpdateMode() {
      return this.mode === "update";
    },
    tinyEditorInit() {
      const self = this;
      return {
        height: 480,
        menubar: false,
        skin: false,
        content_css: false,
        language: "ko_KR",
        branding: false,
        promotion: false,
        elementpath: false,
        plugins: "lists link image table media code autolink",
        toolbar:
          "undo redo | blocks | bold italic underline strikethrough | forecolor backcolor | " +
          "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
          "link image media table | removeformat code",
        default_link_target: "_blank",
        link_default_target: "_blank",
        link_assume_external_targets: true,
        link_target_list: [
          { title: "새 창", value: "_blank" },
          { title: "현재 창", value: "_self" },
        ],
        relative_urls: false,
        convert_urls: false,
        media_live_embeds: true,
        content_style:
          "body{font-family:Pretendard,'NanumSquareRoundR',sans-serif;font-size:14px;line-height:1.6;color:#212529;padding:8px 4px;}" +
          "p{margin:0 0 8px 0;}img{max-width:100%;height:auto;border-radius:6px;}",
        images_upload_handler: (blobInfo) =>
          new Promise((resolve, reject) => {
            const blob = blobInfo.blob();
            const filename = blobInfo.filename();
            if (filename && !blob.name) {
              try {
                Object.defineProperty(blob, "name", {
                  value: filename,
                  configurable: true,
                });
              } catch (_) {
                /* readonly fallback */
              }
            }
            uploadToastImage(blob, "tinymce")
              .then((url) => resolve(url))
              .catch((err) => {
                console.error("TinyMCE 이미지 업로드 실패:", err);
                self.toast("이미지 업로드에 실패했습니다", "danger");
                reject({ message: "이미지 업로드에 실패했습니다", remove: true });
              });
          }),
        file_picker_types: "image",
      };
    },
    showSaveDraftButton() {
      // 이미 등록이 완료된 라이브(= published 상태) 수정 시에는 임시저장 버튼을 숨김
      if (!this.isUpdateMode) return true;
      return this.status === "draft";
    },
    modalTitle() {
      return this.isUpdateMode
        ? "라이브 등록신청 수정"
        : "라이브 등록신청";
    },
    submitButtonText() {
      // 수정 모드라도 임시저장 상태면 최종 버튼은 '등록'
      if (this.isUpdateMode && this.status !== "draft") return "수정";
      return "등록";
    },
    loadingText() {
      // return this.isUpdateMode ? "수정 중..." : "업로드 중...";
      return "";
    },
    // Step 1의 필수 항목이 모두 입력되었는지 확인
    canSaveDraft() {
      // 수정 모드일 때는 항상 임시저장 가능
      if (this.isUpdateMode) {
        return true;
      }

      // 신규 등록 시 Step 1의 필수 항목 확인
      const hasTitle = this.title.trim() !== "";
      const hasType = this.type.trim() !== "";
      const hasPlace = this.place.trim() !== "";
      const hasPoster = this.file1 !== null || this.originalImg !== null;

      return hasTitle && hasType && hasPlace && hasPoster;
    },
    // 최종 등록이 가능한지 확인
    canSubmit() {
      // Step 1 필수 항목
      const hasStep1 = this.canSaveDraft;

      // Step 3 필수 항목
      const hasLink = this.link.trim() !== "";

      // Step 5 예매 정보
      const isLinkook = this.reservationType === "linkook";
      const isExternal = this.reservationType === "external";

      // 외부예매: 현장예매 only 체크 시 예매 링크 없어도 되지만 현장예매가는 필수
      const hasExternalReservationInfo = isExternal && (
        this.onsiteOnly
          ? this.doorPrice.trim() !== ""  // 현장예매 only면 doorPrice 필수
          : this.reservationLink.trim() !== ""  // 아니면 예매 링크 필수
      );

      // 링꾹예매: '현장예매가'를 제외한 모든 항목 필수
      const ticketCount = Number(this.linkookTicketCount);
      const hasLinkookReservationInfo = isLinkook &&
        this.linkookPrice.trim() !== "" &&
        Number.isFinite(ticketCount) &&
        ticketCount >= 1 &&
        ticketCount <= this.maxTicketCount &&
        Number.isFinite(Number(this.linkookPerPersonLimit)) &&
        this.linkookPerPersonLimit >= 1 &&
        this.linkookPerPersonLimit <= 5 &&
        this.bankName.trim() !== "" &&
        this.accountNumber.trim() !== "" &&
        this.accountHolder.trim() !== "" &&
        this.organizerName.trim() !== "" &&
        this.organizerContact.trim() !== "" &&
        !!this.reservationStartDate &&
        !!this.reservationEndDate;

      const hasReservationInfo = hasLinkookReservationInfo || hasExternalReservationInfo;

      return hasStep1 && hasLink && hasReservationInfo;
    },
    pendingCrewRole() {
      return (this.currentCrewRoleInput || "").trim();
    },
    crewSections() {
      const groups = {};
      this.selectedCrew.forEach((person, index) => {
        const rawRole = this.getCrewRole(person) || "기타";
        const roleKey = this.normalizeCrewRoleKey(rawRole);
        const sectionRole = this.findCrewRoleSection(rawRole) || this.normalizeCrewRole(rawRole) || "기타";
        if (!groups[roleKey]) groups[roleKey] = { role: sectionRole, members: [] };
        groups[roleKey].members.push({ person, index });
      });

      const sectionRoles = this.crewRoleSections.length > 0
        ? this.crewRoleSections
        : Object.values(groups).map((group) => group.role);

      return sectionRoles.map((role) => {
        const roleKey = this.normalizeCrewRoleKey(role);
        const group = groups[roleKey];
        return {
          role,
          members: group ? group.members : [],
        };
      });
    },
  },
  beforeUnmount() {
    this.destroyDescriptionEditor();
  },
  watch: {
    liveData: {
      immediate: true,
      async handler(newData) {
        if (newData && this.isUpdateMode) {
          await this.populateForm(newData);
        }
      },
    },
    isVisible: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.formActivated = false; // 창이 열렸지만 아직 로드 중일 수 있음
          // 모달이 열릴 때 항상 첫 단계로 초기화
          this.currentStep = 1;

          if (this.allPlaces.length === 0) {
            await this.fetchPlaces();
          }
          if (this.allArtists.length === 0) {
            await this.fetchArtists();
          }
          // 수정 모드일 때 폼 데이터 복원
          if (this.isUpdateMode && this.liveData) {
            await this.populateForm(this.liveData);
          } else {
            this.$nextTick(() => { this.formActivated = true; });
          }
        } else {
          this.releaseActiveFocus();
          this.formActivated = false;
          this.destroyDescriptionEditor();
        }
      },
    },
    currentStep(val, prev) {
      if (prev === 2 && val !== 2) {
        this.destroyDescriptionEditor();
      }
      if (val === 2) {
        this.$nextTick(() => this.initDescriptionEditor());
      }
    },
    // 현장예매 only 체크 시 예매 링크 초기화
    onsiteOnly(newVal) {
      if (newVal) {
        this.reservationLink = "";
      }
    },
    // 예매 방식 변경 시 onsiteOnly 초기화 / 링꾹 예매 선택 시 준비중 알림
    reservationType(newVal) {
      // LiveForm 창이 활성화된 상태(사용자 입력 가능)일 때만 알림
      if (!this.formActivated) return;
      // 링꾹 예매는 관리자 전용
      if (newVal === "linkook" && this.userStore.auth !== "admin") {
        this.$alert("관리자에게 문의해주세요");
        this.$nextTick(() => {
          // 선택을 되돌려 관리자 전용으로 차단
          this.reservationType = "external";
        });
        return;
      }
      if (newVal !== 'external') {
        this.onsiteOnly = false;
      }
    },
    selectedArtists: {
      handler(newVal) {
        // selectedArtists가 변경될 때마다 member 업데이트 (객체 배열을 문자열로 변환)
        this.member = newVal.map(artist => this.getArtistName(artist)).join(", ");
      },
      deep: true,
    },
  },
  async mounted() {
    await this.fetchPlaces();
    await this.fetchArtists();

    // 드롭다운 패널이 나타날 때 너비 조정
    this.$nextTick(() => {
      const observer = new MutationObserver(() => {
        const placePanel = document.querySelector(".place-autocomplete-panel");
        const artistPanel = document.querySelector(
          ".artist-autocomplete-panel"
        );

        if (
          (placePanel && placePanel.style.display !== "none") ||
          (artistPanel && artistPanel.style.display !== "none")
        ) {
          this.adjustDropdownWidth();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  },
  methods: {
    releaseActiveFocus() {
      if (typeof document === "undefined") return;
      const el = document.activeElement;
      if (el && typeof el.blur === "function") {
        el.blur();
      }
    },
    parseReservationStringToDate(value) {
      if (typeof value !== "string") return null;
      const s = value.trim();
      // YYYY.MM.DD HH:mm (live.date와 동일 포맷)
      const m = /^(\d{4})\.(\d{2})\.(\d{2}) (\d{2}):(\d{2})$/.exec(s);
      if (m) {
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
      }
      // ISO string 등 Date()가 파싱 가능한 값은 Date로 변환
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    },
    /**
     * reservationStartDate/reservationEndDate를 live.date와 동일한 문자열(YYYY.MM.DD HH:mm)로 정규화
     * - Date / Firestore Timestamp / (YYYY.MM.DD HH:mm) string / ISO string 모두 처리
     */
    normalizeReservationDateString(value) {
      if (!value) return null;

      if (typeof value === "string") {
        const s = value.trim();
        if (/^\d{4}\.\d{2}\.\d{2} \d{2}:\d{2}$/.test(s)) return s;
        const d = this.parseReservationStringToDate(s);
        return d ? this.formatDate(d) : null;
      }

      // Firestore Timestamp
      if (value?.toDate && typeof value.toDate === "function") {
        const d = value.toDate();
        return d instanceof Date && !isNaN(d.getTime()) ? this.formatDate(d) : null;
      }

      if (value instanceof Date) {
        return !isNaN(value.getTime()) ? this.formatDate(value) : null;
      }

      return null;
    },
    validateLinkookTicketCountLimit() {
      if (this.reservationType !== "linkook") return true;

      if (this.linkookTicketCount === "" || this.linkookTicketCount === null || this.linkookTicketCount === undefined) {
        return true;
      }

      const count = Number(this.linkookTicketCount);
      if (!Number.isFinite(count)) return true;

      if (count > this.maxTicketCount) {
        this.linkookTicketCount = this.maxTicketCount;
        this.currentStep = 5;
        this.$alert(`티켓 매수는 최대 ${this.maxTicketCount}장까지 가능합니다.`);
        return false;
      }

      if (count < 0) {
        this.linkookTicketCount = 0;
      }

      return true;
    },
    async fetchPlaces() {
      try {
        const placesCollection = collection(firestore, "places");
        const placesSnapshot = await getDocs(placesCollection);

        this.allPlaces = placesSnapshot.docs.map((doc) => {
          const data = doc.data();
          return data.name || doc.id; // name 필드가 있으면 사용, 없으면 document ID 사용
        });

        // 중복 제거 및 정렬
        this.allPlaces = [...new Set(this.allPlaces)].sort();
      } catch (error) {
        console.error("Error fetching places:", error);
        this.allPlaces = [];
      }
    },
    searchPlaces(event) {
      const query = event.query.toLowerCase();
      this.filteredPlaces = this.allPlaces.filter((place) =>
        place.toLowerCase().includes(query)
      );

      // 검색 후 드롭다운 너비 조정
      this.adjustDropdownWidth();
    },
    async fetchArtists() {
      try {
        const artistsCollection = collection(firestore, "artists");
        const artistsSnapshot = await getDocs(artistsCollection);
        const artistsData = [];

        for (const doc of artistsSnapshot.docs) {
          const data = doc.data();
          if (data.approved) {

            artistsData.push({
              id: doc.id,
              name: data.name || doc.id,
              type: data.type || null,
              imgFileName: data.img || null,
              userProfileImg: data.userProfileImg || null,
              profileImage: null,
              profileImageLoaded: false,
              label: data.name || doc.id,
            });
          }
        }

        this.allArtists = artistsData.sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error("Error fetching artists:", error);
        this.allArtists = [];
      }
    },
    async searchArtists(event) {
      const query = event.query.toLowerCase();
      this.filteredArtists = this.allArtists.filter(
        (artist) =>
          artist.name.toLowerCase().includes(query) &&
          !this.selectedArtists.some(a => this.getArtistName(a) === artist.name)
      );
      await this.loadProfileImagesForArtists(this.filteredArtists.slice(0, 20));
    },
    addArtistFromSuggestion(event) {
      const selectedArtist = event.value;
      this.selectedArtistData = selectedArtist;
      this.currentArtistInput = selectedArtist.label;
      this.loadArtistProfileImage(selectedArtist);
      this.addArtistManually();
    },
    addArtistManually() {
      const inputName = (this.currentArtistInput || "").trim();
      const isSelectedMatch = this.selectedArtistData && (
        !inputName ||
        inputName === this.selectedArtistData.name ||
        inputName === this.selectedArtistData.label
      );

      let artistName = "";
      let addedArtist = null;

      if (this.selectedArtistData && isSelectedMatch) {
        artistName = this.selectedArtistData.name;
        addedArtist = {
          name: artistName,
          profileImage: this.selectedArtistData.profileImage || null,
          id: this.selectedArtistData.id || null,
          type: this.selectedArtistData.type || null,
          imgFileName: this.selectedArtistData.imgFileName || null,
          userProfileImg: this.selectedArtistData.userProfileImg || null,
          profileImageLoaded: !!this.selectedArtistData.profileImageLoaded,
          isNew: false,
        };
      } else if (inputName) {
        artistName = inputName;
        addedArtist = {
          name: artistName,
          profileImage: null,
          id: null,
          type: null,
          imgFileName: null,
          userProfileImg: null,
          profileImageLoaded: true,
          isNew: true,
        };
      } else {
        this.toast("아티스트명을 입력해주세요", "warning");
        return;
      }

      // 중복 체크
      if (this.selectedArtists.some(a => this.getArtistName(a) === artistName)) {
        this.toast("이미 추가된 아티스트입니다", "warning");
        return;
      }

      this.selectedArtists.push(addedArtist);
      if (addedArtist.id || addedArtist.imgFileName || addedArtist.userProfileImg) {
        this.loadArtistProfileImage(addedArtist);
      }

      this.currentArtistInput = "";
      this.selectedArtistData = null;
      this.filteredArtists = [];
    },

    clearArtistInput() {
      // v-model 초기화
      this.currentArtistInput = "";

      // AutoComplete 컴포넌트 강제 초기화
      this.$nextTick(() => {
        if (this.$refs.artistAutocompleteRef) {
          const autocompleteEl = this.$refs.artistAutocompleteRef;
          // PrimeVue AutoComplete의 내부 input 접근
          const inputEl =
            autocompleteEl.$el.querySelector("input") || autocompleteEl.$el;
          if (inputEl && inputEl.tagName === "INPUT") {
            inputEl.value = "";
            inputEl.focus();
            // 커서를 처음으로 이동
            inputEl.setSelectionRange(0, 0);
          }
        }
      });
    },
    removeArtist(index) {
      this.selectedArtists.splice(index, 1);
    },
    // 함께한 사람들 (라인업과 동일 양식)
    normalizeCrewRole(role) {
      return (role || "").trim().replace(/\s+/g, " ");
    },
    normalizeCrewRoleKey(role) {
      return this.normalizeCrewRole(role).toLowerCase();
    },
    findCrewRoleSection(role) {
      const key = this.normalizeCrewRoleKey(role);
      if (!key) return "";
      return this.crewRoleSections.find((sectionRole) => this.normalizeCrewRoleKey(sectionRole) === key) || "";
    },
    addCrewRoleSection() {
      const role = this.normalizeCrewRole(this.pendingCrewRole);
      if (!role) {
        this.toast("역할명을 입력해주세요", "warning");
        return;
      }
      const existingRole = this.findCrewRoleSection(role);
      if (existingRole) {
        this.activeCrewRole = existingRole;
        this.toast("이미 추가된 역할 섹션입니다", "warning");
        return;
      }

      this.crewRoleSections.push(role);
      this.activeCrewRole = role;
      this.currentCrewRoleInput = "";
      this.filteredCrewRoles = [];
    },
    searchCrewRoles(event) {
      const query = (event.query || "").toLowerCase();
      this.filteredCrewRoles = this.crewRoleOptions.filter((role) =>
        role.toLowerCase().includes(query)
      );
    },
    addCrewRoleFromSuggestion(event) {
      const selectedRole = typeof event.value === "string" ? event.value : "";
      this.currentCrewRoleInput = selectedRole;
      this.addCrewRoleSection();
    },
    setActiveCrewRole(role) {
      this.activeCrewRole = this.findCrewRoleSection(role) || this.normalizeCrewRole(role);
      this.filteredCrew = [];
      this.selectedCrewData = null;
      this.currentCrewInput = "";
    },
    async removeCrewRoleSection(role) {
      this.releaseActiveFocus();
      const targetRole = this.findCrewRoleSection(role) || this.normalizeCrewRole(role);
      const targetRoleKey = this.normalizeCrewRoleKey(targetRole);
      const hasMembers = this.selectedCrew.some(
        (person) => this.normalizeCrewRoleKey(this.getCrewRole(person)) === targetRoleKey
      );
      if (hasMembers) {
        const confirmed = await this.$confirm(`${targetRole} 섹션의 인원도 함께 삭제됩니다. 진행할까요?`);
        if (!confirmed) return;
      }

      this.crewRoleSections = this.crewRoleSections.filter(
        (r) => this.normalizeCrewRoleKey(r) !== targetRoleKey
      );
      this.selectedCrew = this.selectedCrew.filter(
        (person) => this.normalizeCrewRoleKey(this.getCrewRole(person)) !== targetRoleKey
      );
      delete this.crewInputByRole[targetRole];
      delete this.crewSelectedDataByRole[targetRole];
      delete this.filteredCrewByRole[targetRole];

      if (this.normalizeCrewRoleKey(this.activeCrewRole) === targetRoleKey) {
        this.activeCrewRole = this.crewRoleSections[0] || "";
      }
    },
    getCrewInputByRole(role) {
      return (this.crewInputByRole[role] || "").trim();
    },
    canAddCrewForRole(role) {
      return !!(this.crewSelectedDataByRole[role] || this.getCrewInputByRole(role));
    },
    async searchCrewByRole(event, role) {
      const query = (event.query || "").toLowerCase();
      const roleKey = this.normalizeCrewRoleKey(role);
      this.filteredCrewByRole[role] = this.allArtists.filter(
        (artist) =>
          artist.name.toLowerCase().includes(query) &&
          !this.selectedCrew.some(
            (c) =>
              this.getCrewName(c) === artist.name &&
              this.normalizeCrewRoleKey(this.getCrewRole(c)) === roleKey
          )
      );
      await this.loadProfileImagesForArtists((this.filteredCrewByRole[role] || []).slice(0, 20));
    },
    addCrewFromSuggestionByRole(event, role) {
      const selected = event.value;
      this.crewSelectedDataByRole[role] = selected;
      this.crewInputByRole[role] = selected.label;
      this.loadArtistProfileImage(selected);
      this.addCrewManuallyByRole(role);
    },
    addCrewManuallyByRole(role) {
      const inputName = (this.crewInputByRole[role] || "").trim();
      const inputRole = this.findCrewRoleSection(role) || this.normalizeCrewRole(role);
      const inputRoleKey = this.normalizeCrewRoleKey(inputRole);
      const selectedData = this.crewSelectedDataByRole[role];
      const isSelectedMatch = selectedData && (
        !inputName ||
        inputName === selectedData.name ||
        inputName === selectedData.label
      );

      let name = "";
      let added = null;

      if (selectedData && isSelectedMatch) {
        name = selectedData.name;
        added = {
          name,
          profileImage: selectedData.profileImage || null,
          id: selectedData.id || null,
          type: selectedData.type || null,
          imgFileName: selectedData.imgFileName || null,
          userProfileImg: selectedData.userProfileImg || null,
          profileImageLoaded: !!selectedData.profileImageLoaded,
          isNew: false,
        };
      } else if (inputName) {
        name = inputName;
        added = {
          name,
          profileImage: null,
          id: null,
          type: null,
          imgFileName: null,
          userProfileImg: null,
          profileImageLoaded: true,
          isNew: true,
        };
      } else {
        this.toast("이름을 입력해주세요", "warning");
        return;
      }

      if (!inputRole) {
        this.toast("역할명을 입력해주세요", "warning");
        return;
      }

      added.role = inputRole;

      if (
        this.selectedCrew.some(
          (c) =>
            this.getCrewName(c) === name &&
            this.normalizeCrewRoleKey(this.getCrewRole(c)) === inputRoleKey
        )
      ) {
        this.toast("해당 역할에 이미 추가된 이름입니다", "warning");
        return;
      }

      this.selectedCrew.push(added);
      if (inputRole && !this.crewRoleSections.includes(inputRole)) {
        this.crewRoleSections.push(inputRole);
      }
      if (added.id || added.imgFileName || added.userProfileImg) {
        this.loadArtistProfileImage(added);
      }
      this.crewInputByRole[role] = "";
      this.crewSelectedDataByRole[role] = null;
      this.filteredCrewByRole[role] = [];
    },
    async searchCrew(event) {
      if (!this.activeCrewRole) return;
      await this.searchCrewByRole(event, this.activeCrewRole);
      this.filteredCrew = this.filteredCrewByRole[this.activeCrewRole] || [];
    },
    addCrewFromSuggestion(event) {
      if (!this.activeCrewRole) return;
      this.addCrewFromSuggestionByRole(event, this.activeCrewRole);
    },
    addCrewManually() {
      if (!this.activeCrewRole) return;
      this.addCrewManuallyByRole(this.activeCrewRole);
    },
    removeCrew(index) {
      this.selectedCrew.splice(index, 1);
    },
    getCrewName(person) {
      if (typeof person === "object" && person !== null) {
        return person.name || "";
      }
      return person || "";
    },
    getCrewRole(person) {
      if (typeof person === "object" && person !== null) {
        return this.normalizeCrewRole(person.role || "");
      }
      return "";
    },
    buildCrewPayload(person) {
      return {
        name: this.getCrewName(person),
        role: this.getCrewRole(person) || null,
      };
    },
    // 아티스트 이름 가져오기 (객체 또는 문자열 처리)
    getArtistName(artist) {
      if (typeof artist === 'object' && artist !== null) {
        return artist.name || '';
      }
      return artist || '';
    },
    formatArtistType(type) {
      return getArtistTypeDisplayName(type);
    },
    getArtistDefaultImage(type) {
      try {
        const iconKey = getArtistTypeIconKey(type);
        return require(`@/assets/images/${iconKey}.png`);
      } catch {
        return require("@/assets/images/default_profile.png");
      }
    },
    getArtistImagePath(artist) {
      if (!artist) return null;
      if (artist.userProfileImg) {
        return `profile/${artist.userProfileImg}`;
      }
      if (artist.imgFileName) {
        return `artist/${artist.imgFileName}`;
      }
      return null;
    },
    syncArtistImageToAllArtists(artist) {
      if (!artist) return;
      const baseArtist = this.allArtists.find((a) => a.id === artist.id || a.name === artist.name);
      if (!baseArtist || baseArtist === artist) return;
      baseArtist.profileImage = artist.profileImage || null;
      baseArtist.profileImageLoaded = true;
    },
    async loadArtistProfileImage(artist) {
      if (!artist || artist.profileImageLoaded) return;
      if (artist.profileImage && typeof artist.profileImage === "string") {
        artist.profileImageLoaded = true;
        this.syncArtistImageToAllArtists(artist);
        return;
      }

      const imagePath = this.getArtistImagePath(artist);
      if (!imagePath) {
        artist.profileImageLoaded = true;
        this.syncArtistImageToAllArtists(artist);
        return;
      }

      const cacheKey = artist.id || artist.name || imagePath;
      if (this.artistImageLoadPromises[cacheKey]) {
        await this.artistImageLoadPromises[cacheKey];
        if (!artist.profileImage) {
          const baseArtist = this.allArtists.find((a) => a.id === artist.id || a.name === artist.name);
          if (baseArtist?.profileImage) {
            artist.profileImage = baseArtist.profileImage;
          }
        }
        artist.profileImageLoaded = true;
        return;
      }

      const loadPromise = (async () => {
        try {
          const imageRef = storageRef(storage, imagePath);
          artist.profileImage = await getDownloadURL(imageRef);
        } catch (error) {
          artist.profileImage = null;
        } finally {
          artist.profileImageLoaded = true;
          this.syncArtistImageToAllArtists(artist);
        }
      })();

      this.artistImageLoadPromises[cacheKey] = loadPromise;
      try {
        await loadPromise;
      } finally {
        delete this.artistImageLoadPromises[cacheKey];
      }
    },
    async loadProfileImagesForArtists(artists) {
      if (!Array.isArray(artists) || artists.length === 0) return;
      const uniqueArtists = [];
      const seen = new Set();

      for (const artist of artists) {
        const key = artist?.id || artist?.name;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        uniqueArtists.push(artist);
      }

      await Promise.all(uniqueArtists.map((artist) => this.loadArtistProfileImage(artist)));
    },
    async populateForm(liveData) {
      try {
        this.title = liveData.title || "";
        this.place = liveData.place || "";
        this.type = liveData.type || "";
        this.link = liveData.link || "";
        this.reservationLink = liveData.reservationLink || "";
        // organizer가 배열인 경우 첫 번째 값, 문자열인 경우 그대로 사용
        this.organizer = Array.isArray(liveData.organizer)
          ? (liveData.organizer[0] || "")
          : (liveData.organizer || "");
        this.linkookReservation = liveData.linkookReservation || false;
        // reservationType 설정: linkookReservation이 true면 'linkook', 아니면 'external'
        this.reservationType = liveData.linkookReservation ? 'linkook' : 'external';
        this.onsiteOnly = liveData.onsiteOnly || false;
        this.linkookPrice = liveData.linkookPrice || "";
        this.linkookTicketCount = liveData.linkookTicketCount || "";
        this.linkookPerPersonLimit = liveData.linkookPerPersonLimit ?? 2;
        this.bankName = liveData.bankName || "";
        this.accountNumber = liveData.accountNumber || "";
        this.accountHolder = liveData.accountHolder || "";
        // DatePicker v-model은 Date가 가장 안정적이므로, 문자열도 Date로 변환해서 세팅
        this.reservationStartDate = liveData.reservationStartDate?.toDate
          ? liveData.reservationStartDate.toDate()
          : (typeof liveData.reservationStartDate === "string"
            ? this.parseReservationStringToDate(liveData.reservationStartDate)
            : (liveData.reservationStartDate || null));
        this.reservationEndDate = liveData.reservationEndDate?.toDate
          ? liveData.reservationEndDate.toDate()
          : (typeof liveData.reservationEndDate === "string"
            ? this.parseReservationStringToDate(liveData.reservationEndDate)
            : (liveData.reservationEndDate || null));
        this.advancePrice = liveData.advancePrice || "";
        this.doorPrice = liveData.doorPrice || "";
        this.member = liveData.lineup ? liveData.lineup.join(", ") : "";
        this.posterCreatorNickname = liveData.posterCreatorNickname || "";
        this.posterDetailLink = liveData.posterDetailLink || "";
        this.organizerName = liveData.organizerName || "";
        this.organizerContact = liveData.organizerContact || "";
        this.status = liveData.status || "published";
        this.youtubeVideos = Array.isArray(liveData.youtubeVideos) ? liveData.youtubeVideos : [];

        // 라인업 배열을 selectedArtists에 설정 (기존 문자열 배열과 새로운 객체 배열 모두 처리)
        if (liveData.lineup && liveData.lineup.length > 0) {
          this.selectedArtists = [];
          for (const item of liveData.lineup) {
            let artistName, artistProfileImage = null, artistId = null, artistType = null;
            let imgFileName = null, userProfileImg = null, profileImageLoaded = false;

            // 이미 객체 형태면 기존 정보 사용
            if (typeof item === 'object' && item !== null) {
              artistName = item.name || '';
              artistProfileImage = item.profileImage || null;
              artistId = item.id || null;
              artistType = item.type || null;
              imgFileName = item.imgFileName || item.img || null;
              userProfileImg = item.userProfileImg || null;
              profileImageLoaded = !!artistProfileImage;
            } else {
              // 문자열이면 아티스트 이름으로 처리
              artistName = item;
            }

            // allArtists에서 프로필 이미지 찾기 (아직 없으면)
            if (!artistProfileImage && artistName) {
              const artistInfo = this.allArtists.find(a => a.name === artistName);
              if (artistInfo) {
                artistProfileImage = artistInfo.profileImage;
                artistId = artistInfo.id;
                artistType = artistType || artistInfo.type || null;
                imgFileName = imgFileName || artistInfo.imgFileName || null;
                userProfileImg = userProfileImg || artistInfo.userProfileImg || null;
                profileImageLoaded = profileImageLoaded || !!artistInfo.profileImageLoaded;
              }
            }

            this.selectedArtists.push({
              name: artistName,
              profileImage: artistProfileImage,
              id: artistId,
              type: artistType,
              imgFileName,
              userProfileImg,
              profileImageLoaded,
              isNew: false,
            });
          }
          await this.loadProfileImagesForArtists(this.selectedArtists);
        } else {
          this.selectedArtists = [];
        }

        // 함께한 사람들 (crew) 복원
        if (liveData.crew && liveData.crew.length > 0) {
          this.selectedCrew = [];
          for (const item of liveData.crew) {
            let personName, profileImage = null, personId = null, personType = null;
            let personRole = "";
            let imgFileName = null, userProfileImg = null, profileImageLoaded = false;
            if (typeof item === "object" && item !== null) {
              personName = item.name || "";
              personRole = this.normalizeCrewRole(item.role || item.part || "");
              profileImage = item.profileImage || null;
              personId = item.id || null;
              personType = item.type || null;
              imgFileName = item.imgFileName || item.img || null;
              userProfileImg = item.userProfileImg || null;
              profileImageLoaded = !!profileImage;
            } else {
              personName = item;
            }
            if (!profileImage && personName) {
              const artistInfo = this.allArtists.find((a) => a.name === personName);
              if (artistInfo) {
                profileImage = artistInfo.profileImage;
                personId = artistInfo.id;
                personType = personType || artistInfo.type || null;
                imgFileName = imgFileName || artistInfo.imgFileName || null;
                userProfileImg = userProfileImg || artistInfo.userProfileImg || null;
                profileImageLoaded = profileImageLoaded || !!artistInfo.profileImageLoaded;
              }
            }
            this.selectedCrew.push({
              name: personName,
              role: personRole,
              profileImage,
              id: personId,
              type: personType,
              imgFileName,
              userProfileImg,
              profileImageLoaded,
              isNew: false,
            });
          }
          const dedupMap = new Map();
          this.selectedCrew
            .map((person) => this.getCrewRole(person))
            .filter(Boolean)
            .forEach((role) => {
              const key = this.normalizeCrewRoleKey(role);
              if (!dedupMap.has(key)) {
                dedupMap.set(key, role);
              }
            });
          this.crewRoleSections = [...dedupMap.values()];
          this.activeCrewRole = this.crewRoleSections[0] || "";
          await this.loadProfileImagesForArtists(this.selectedCrew);
        } else {
          this.selectedCrew = [];
          this.crewRoleSections = [];
          this.activeCrewRole = "";
        }

        // 날짜 파싱
        if (liveData.date) {
          const dateStr = liveData.date.replace(/\./g, "-");
          this.selectedDateTime = new Date(dateStr);
        }

        // 입장 일시 파싱
        if (liveData.entryTime) {
          const entryStr = liveData.entryTime.replace(/\./g, "-");
          this.entryDateTime = new Date(entryStr);
        } else {
          this.entryDateTime = null;
        }

        // 기존 이미지가 있으면 미리보기 URL 가져오기 (원본 값 사용)
        if (liveData.img) {
          this.posterPreviewUrl = await this.getImagePreviewUrl("poster", liveData.img);
        }
        if (liveData.banner) {
          this.bannerPreviewUrl = await this.getImagePreviewUrl("banner", liveData.banner);
        }
        if (liveData.detail_img) {
          // detail 이미지는 detailImage 경로에 저장될 수 있음
          this.detailImgPreviewUrl = await this.getImagePreviewUrl("live-detail", liveData.detail_img)
        }
        if (liveData.detail_img2) {
          // detail 이미지는 detailImage 경로에 저장될 수 있음
          this.detailImg2PreviewUrl = await this.getImagePreviewUrl("live-detail", liveData.detail_img2)
        }
        if (liveData.detail_img3) {
          this.detailImg3PreviewUrl = await this.getImagePreviewUrl("live-detail", liveData.detail_img3)
        }
        if (liveData.detail_img4) {
          this.detailImg4PreviewUrl = await this.getImagePreviewUrl("live-detail", liveData.detail_img4)
        }

        // 기존 이미지 파일명 저장 (URL인 경우 파일명만 추출하여 저장)
        this.originalImg = this.extractFileName(liveData.img);
        this.originalBanner = this.extractFileName(liveData.banner);
        this.originalDetailImg = this.extractFileName(liveData.detail_img);
        this.originalDetailImg2 = this.extractFileName(liveData.detail_img2);
        this.originalDetailImg3 = this.extractFileName(liveData.detail_img3);
        this.originalDetailImg4 = this.extractFileName(liveData.detail_img4);

        // 삭제 플래그 초기화 (포스터는 삭제 불가)
        this.deletedBanner = false;
        this.deletedDetailImg = false;
        this.deletedDetailImg2 = false;
        this.deletedDetailImg3 = false;
        this.deletedDetailImg4 = false;

        this.description = liveData.description || "";
        this.descriptionHtml = liveData.descriptionHtml || "";
      } finally {
        this.$nextTick(() => {
          this.formActivated = true;
          this.syncDescriptionEditorFromModel();
        });
      }
    },
    initDescriptionEditor() {
      const el = this.$refs.descriptionEditorMount;
      if (!el || this._descriptionToastEditor) return;
      this._descriptionToastEditor = new Editor({
        el,
        height: "480px",
        initialEditType: "wysiwyg",
        initialValue: prepareToastViewerMarkdown(this.description || ""),
        usageStatistics: false,
        hideModeSwitch: true,
        language: "ko-KR",
        autofocus: false,
        placeholder: "예: 공연 소개, 셋리스트, 관람 안내 등",
        extendedAutolinks: true,
        linkAttributes: { target: "_blank", rel: "noopener noreferrer" },
        plugins: [colorSyntax, tableMergedCell],
        toolbarItems: [
          ["heading", "bold", "italic", "strike"],
          ["hr", "quote"],
          ["ul", "ol", "task"],
          ["table", "code", "codeblock"],
          ["link", "image"],
        ],
        hooks: {
          addImageBlobHook: async (blob, callback) => {
            try {
              const url = await uploadToastImage(blob, "toastui");
              const altText = (blob && blob.name) ? blob.name : "image";
              callback(url, altText);
            } catch (err) {
              console.error("Toast UI 이미지 업로드 실패:", err);
              this.toast("이미지 업로드에 실패했습니다", "danger");
            }
          },
        },
      });
      this._descriptionToastEditor.on("change", () => {
        this.description = this._descriptionToastEditor.getMarkdown();
      });
    },
    destroyDescriptionEditor() {
      if (!this._descriptionToastEditor) return;
      try {
        this.description = this._descriptionToastEditor.getMarkdown();
      } catch (_) {
        /* noop */
      }
      try {
        this._descriptionToastEditor.destroy();
      } catch (_) {
        /* noop */
      }
      this._descriptionToastEditor = null;
    },
    syncDescriptionEditorFromModel() {
      if (!this._descriptionToastEditor) return;
      try {
        this._descriptionToastEditor.setMarkdown(prepareToastViewerMarkdown(this.description || ""));
      } catch (_) {
        /* noop */
      }
    },
    handleFileUpload1(event) {
      const file = event.target.files[0];
      if (file) {
        this.file1 = file;
        // 새 파일 선택 시 미리보기 업데이트
        const reader = new FileReader();
        reader.onload = (e) => {
          this.posterPreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    handleFileUpload2(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        this.cropSrc = e.target.result;
        this.cropFile = file;
        this.showCropModal = true;
        await nextTick();
        window.setTimeout(() => { this._initCropper(); }, 300);
      };
      reader.readAsDataURL(file);

      if (this.$refs.bannerFileInput) {
        this.$refs.bannerFileInput.value = "";
      }
    },

    _initCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      const img = this.$refs.cropImage;
      if (!img) return;
      this.cropper = new Cropper(img, {
        aspectRatio: 1450 / 485,
        viewMode: 1,
        autoCropArea: 1,
        movable: true,
        zoomable: true,
        scalable: false,
        responsive: true,
      });
    },

    cancelCrop() {
      if (this.cropper) { this.cropper.destroy(); this.cropper = null; }
      this.showCropModal = false;
      this.cropSrc = null;
      this.cropFile = null;
    },

    confirmCrop() {
      if (!this.cropper) return;
      const canvas = this.cropper.getCroppedCanvas({ width: 1450, height: 485 });
      const originalFile = this.cropFile;
      if (this.cropper) { this.cropper.destroy(); this.cropper = null; }
      this.showCropModal = false;
      this.cropSrc = null;
      this.cropFile = null;

      canvas.toBlob((blob) => {
        if (!blob) { alert("크롭 처리에 실패했습니다."); return; }
        const ext = (originalFile?.name || "banner.jpg").split(".").pop() || "jpg";
        const croppedFile = new File([blob], originalFile?.name || `banner-cropped.${ext}`, { type: blob.type });

        this.file2 = croppedFile;
        this.bannerPreviewUrl = canvas.toDataURL();
        this.deletedBanner = false;
      }, originalFile?.type || "image/jpeg");
    },
    handleDetailImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.detailImageFile = file;
        // 새 파일 선택 시 미리보기 업데이트
        const reader = new FileReader();
        reader.onload = (e) => {
          this.detailImgPreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
        // 삭제 플래그 해제
        this.deletedDetailImg = false;
      }
    },
    handleDetailImage2Upload(event) {
      const file = event.target.files[0];
      if (file) {
        this.detailImage2File = file;
        // 새 파일 선택 시 미리보기 업데이트
        const reader = new FileReader();
        reader.onload = (e) => {
          this.detailImg2PreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
        // 삭제 플래그 해제
        this.deletedDetailImg2 = false;
      }
    },
    handleDetailImage3Upload(event) {
      const file = event.target.files[0];
      if (file) {
        this.detailImage3File = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.detailImg3PreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
        this.deletedDetailImg3 = false;
      }
    },
    handleDetailImage4Upload(event) {
      const file = event.target.files[0];
      if (file) {
        this.detailImage4File = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.detailImg4PreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
        this.deletedDetailImg4 = false;
      }
    },
    // URL에서 파일명만 추출하는 헬퍼 함수
    extractFileName(value) {
      if (!value) return null;

      // HTTP URL이 아닌 경우 그대로 반환 (이미 파일명)
      if (!value.startsWith("http")) {
        return value;
      }

      try {
        // Firebase Storage URL에서 파일명 추출
        // 예: https://firebasestorage.googleapis.com/v0/b/[bucket]/o/poster%2Ffilename.jpg?alt=media
        const url = new URL(value);
        const pathname = url.pathname;

        // 경로에서 마지막 부분 추출 (예: poster%2Ffilename.jpg)
        const pathParts = pathname.split('/');
        const encodedPath = pathParts[pathParts.length - 1];

        // URL 디코딩 (예: poster/filename.jpg)
        const decodedPath = decodeURIComponent(encodedPath);

        // 마지막 슬래시 이후의 파일명 추출
        const fileName = decodedPath.split('/').pop();

        return fileName || value; // 파일명을 찾지 못하면 원본 반환
      } catch (error) {
        console.error("파일명 추출 실패:", error);
        // URL 파싱 실패 시 원본 반환
        return value;
      }
    },
    async getImagePreviewUrl(path, fileName) {
      if (!fileName) return null;

      // 이미 HTTP URL인 경우 그대로 반환
      if (fileName.startsWith("http")) {
        return fileName;
      }

      // Storage에서 다운로드 URL 가져오기
      try {
        const imageRef = storageRef(storage, `${path}/${fileName}`);
        const url = await getDownloadURL(imageRef);
        return url;
      } catch (error) {
        console.error(`${path} 이미지 로드 실패 (경로: ${path}/${fileName}):`, error);
        return null;
      }
    },
    async deleteBannerImage() {
      this.releaseActiveFocus();
      const confirmed = await this.$confirm("배너 이미지를 삭제하시겠습니까?");
      if (confirmed) {
        this.originalBanner = null;
        this.bannerPreviewUrl = null;
        this.deletedBanner = true;
        this.file2 = null;
        // 파일 입력 초기화
        if (this.$refs.bannerFileInput) {
          this.$refs.bannerFileInput.value = "";
        }
      }
    },
    async deleteDetailImage() {
      this.releaseActiveFocus();
      const confirmed = await this.$confirm("공연상세정보 1 이미지를 삭제하시겠습니까?");
      if (confirmed) {
        this.originalDetailImg = null;
        this.detailImgPreviewUrl = null;
        this.deletedDetailImg = true;
        this.detailImageFile = null;
        // 파일 입력 초기화
        if (this.$refs.detailImageFileInput) {
          this.$refs.detailImageFileInput.value = "";
        }
      }
    },
    async deleteDetailImage2() {
      this.releaseActiveFocus();
      const confirmed = await this.$confirm("공연상세정보 2 이미지를 삭제하시겠습니까?");
      if (confirmed) {
        this.originalDetailImg2 = null;
        this.detailImg2PreviewUrl = null;
        this.deletedDetailImg2 = true;
        this.detailImage2File = null;
        // 파일 입력 초기화
        if (this.$refs.detailImage2FileInput) {
          this.$refs.detailImage2FileInput.value = "";
        }
      }
    },
    async deleteDetailImage3() {
      this.releaseActiveFocus();
      const confirmed = await this.$confirm("공연상세정보 3 이미지를 삭제하시겠습니까?");
      if (confirmed) {
        this.originalDetailImg3 = null;
        this.detailImg3PreviewUrl = null;
        this.deletedDetailImg3 = true;
        this.detailImage3File = null;
        if (this.$refs.detailImage3FileInput) {
          this.$refs.detailImage3FileInput.value = "";
        }
      }
    },
    async deleteDetailImage4() {
      this.releaseActiveFocus();
      const confirmed = await this.$confirm("공연상세정보 4 이미지를 삭제하시겠습니까?");
      if (confirmed) {
        this.originalDetailImg4 = null;
        this.detailImg4PreviewUrl = null;
        this.deletedDetailImg4 = true;
        this.detailImage4File = null;
        if (this.$refs.detailImage4FileInput) {
          this.$refs.detailImage4FileInput.value = "";
        }
      }
    },
    async handleSubmit() {
      // 최종 등록 전 필수 항목 확인 → 미충족 항목 모두 리스팅 후 alert
      if (!this.canSubmit) {
        const missing = [];

        // 1단계
        if (this.title.trim() === "") missing.push({ step: 1, label: "라이브 제목" });
        if (this.type.trim() === "") missing.push({ step: 1, label: "공연타입" });
        if (this.place.trim() === "") missing.push({ step: 1, label: "장소" });
        if (this.file1 === null && !this.originalImg) missing.push({ step: 1, label: "포스터" });

        // 3단계
        if (this.link.trim() === "") missing.push({ step: 3, label: "공연 정보 링크" });
        if (this.reservationType === "linkook" && this.organizer.trim() === "") {
          missing.push({ step: 3, label: "주최자 (이메일 주소)" });
        }

        // 5단계
        if (this.reservationType !== "linkook" && this.reservationType !== "external") {
          missing.push({ step: 5, label: "예매 방식 (링꾹예매 또는 외부예매)" });
        } else if (this.reservationType === "external") {
          if (this.onsiteOnly) {
            // 현장예매 only인 경우 현장예매가 필수
            if (this.doorPrice.trim() === "") {
              missing.push({ step: 5, label: "현장예매가" });
            }
          } else {
            // 일반 외부예매인 경우 예매 링크 필수
            if (this.reservationLink.trim() === "") {
              missing.push({ step: 5, label: "예매 링크 또는 현장예매 only 선택" });
            }
          }
        } else if (this.reservationType === "linkook") {
          if (this.linkookPrice.trim() === "") missing.push({ step: 5, label: "링꾹예매가" });

          const ticketCount = Number(this.linkookTicketCount);
          if (
            this.linkookTicketCount === "" ||
            this.linkookTicketCount === null ||
            this.linkookTicketCount === undefined ||
            !Number.isFinite(ticketCount) ||
            ticketCount < 1 ||
            ticketCount > this.maxTicketCount
          ) {
            missing.push({ step: 5, label: `링꾹티켓수 (1~${this.maxTicketCount}장)` });
          }

          if (!Number.isFinite(Number(this.linkookPerPersonLimit)) || this.linkookPerPersonLimit < 1 || this.linkookPerPersonLimit > 5) {
            missing.push({ step: 5, label: "인당 구매제한 (1~5장)" });
          }
          if (this.bankName.trim() === "") missing.push({ step: 5, label: "정산 은행" });
          if (this.accountNumber.trim() === "") missing.push({ step: 5, label: "정산 계좌번호" });
          if (this.accountHolder.trim() === "") missing.push({ step: 5, label: "정산 예금주" });
          if (this.organizerName.trim() === "") missing.push({ step: 5, label: "주최자명" });
          if (this.organizerContact.trim() === "") missing.push({ step: 5, label: "주최자 연락처" });
          if (!this.reservationStartDate) missing.push({ step: 5, label: "예매 시작일" });
          if (!this.reservationEndDate) missing.push({ step: 5, label: "예매 종료일" });
        }

        if (missing.length === 0) return; // 방어 코드

        const stepGroups = {};
        missing.forEach(({ step, label }) => {
          if (!stepGroups[step]) stepGroups[step] = [];
          stepGroups[step].push(label);
        });
        const lines = ["필수 입력 항목을 모두 채워주세요.\n"];
        [1, 3, 4].forEach((step) => {
          if (stepGroups[step]) {
            lines.push(`[${step}단계]`);
            stepGroups[step].forEach((label) => lines.push(`  · ${label}`));
            lines.push("");
          }
        });
        const message = lines.join("\n").trimEnd();
        this.currentStep = missing[0].step;
        this.$alert(message);
        return;
      }

      this.status = "published"; // 발행 버튼은 published 상태로
      if (this.isUpdateMode) {
        await this.update();
      } else {
        await this.upload();
      }
    },
    async handleSaveDraft() {
      // Step 1의 필수 항목 검사
      if (!this.canSaveDraft) {
        this.toast("첫 번째 페이지의 필수 항목(포스터, 제목, 타입, 장소)을 모두 입력해주세요", "default");
        return;
      }

      this.status = "draft"; // 임시저장 버튼은 draft 상태로
      if (this.isUpdateMode) {
        await this.update();
      } else {
        await this.upload();
      }
    },
    async upload() {
      this.releaseActiveFocus();
      // URL 유효성 검사 (임시저장/최종등록 모두 수행)
      if (this.link.trim() !== "") {
        const normalizedLink = this.normalizeUrl(this.link);
        if (!this.checkUrl(normalizedLink)) {
          this.toast("올바른 공연 정보 링크를 입력해주세요", "default");
          return;
        }
      }

      if (!this.validateLinkookTicketCountLimit()) {
        return;
      }

      // 선택 링크들도 정규화
      const normalizedLink = this.link.trim() ? this.normalizeUrl(this.link) : "";
      const normalizedReservationLink = this.reservationLink ? this.normalizeUrl(this.reservationLink) : "";
      const normalizedPosterDetailLink = this.posterDetailLink.trim() ? this.normalizeUrl(this.posterDetailLink) : null;

      const liveData = {
        title: this.title,
        date: this.formatDate(this.selectedDateTime),
        entryTime: this.entryDateTime
          ? this.formatDate(this.entryDateTime)
          : null,
        place: this.place,
        link: normalizedLink,
        reservationLink: normalizedReservationLink,
        organizer: [this.userStore.email],
        linkookReservation: this.reservationType === 'linkook',
        onsiteOnly: this.onsiteOnly || false,
        linkookPrice: this.linkookPrice,
        linkookTicketCount: this.linkookTicketCount,
        linkookPerPersonLimit: this.linkookPerPersonLimit,
        bankName: this.bankName.trim() || null,
        accountNumber: this.accountNumber.trim() || null,
        accountHolder: this.accountHolder.trim() || null,
        // 예매 기간은 공연일시(date)와 동일하게 문자열로 저장 (YYYY.MM.DD HH:mm)
        reservationStartDate: this.normalizeReservationDateString(this.reservationStartDate),
        reservationEndDate: this.normalizeReservationDateString(this.reservationEndDate),
        advancePrice: this.advancePrice,
        doorPrice: this.doorPrice,
        lineup: this.selectedArtists.map(artist => this.getArtistName(artist)),
        crew: this.selectedCrew.map((person) => this.buildCrewPayload(person)),
        crews: this.selectedCrew.map((person) => this.getCrewName(person)).join(", "),
        type: this.type,
        img: this.file1.name,
        banner: this.file2 ? this.file2.name : "",
        detail_img: this.detailImageFile ? this.detailImageFile.name : "",
        detail_img2: this.detailImage2File ? this.detailImage2File.name : "",
        detail_img3: this.detailImage3File ? this.detailImage3File.name : "",
        detail_img4: this.detailImage4File ? this.detailImage4File.name : "",
        description: this.description?.trim() || "",
        descriptionHtml: this.descriptionHtml?.trim() || "",
        posterCreatorNickname: this.posterCreatorNickname.trim() || null,
        posterDetailLink: normalizedPosterDetailLink,
        organizerName: this.organizerName.trim() || null,
        organizerContact: this.organizerContact.trim() || null,
        youtubeVideos: this.youtubeVideos.map(v => ({
          vid: v.vid,
          url: v.url,
          title: v.title,
          channel: v.channel,
          channelId: v.channelId || null,
          channelThumbnail: v.channelThumbnail || null,
          channelHandle: v.channelHandle || null,
          videoThumbnail: v.videoThumbnail || null,
          publishedAt: v.publishedAt || null,
        })),
        approved: false,
        status: this.status, // 'draft' 또는 'published'
        reg_id: this.userStore.email,
        reg_nickname: this.userStore.nickname,
        createdBy: this.userStore.email,
        createdByNickname: this.userStore.nickname,
        createdAt: new Date(),
      };

      try {
        this.uploading = true;

        const liveId = await createLive(
          liveData,
          this.file1,
          this.file2,
          this.detailImageFile,
          this.detailImage2File,
          this.detailImage3File,
          this.detailImage4File
        );

        // 신규 아티스트를 artists 컬렉션에 자동 저장 (아티스트가 있을 때만)
        if (this.selectedArtists.length > 0) {
          const artistNames = this.selectedArtists.map(artist => this.getArtistName(artist));
          await saveNewArtists(artistNames, this.type);
        }

        // 유튜브 영상을 videos 컬렉션에 저장
        if (this.youtubeVideos.length > 0) {
          await this.saveVideosToFirestore(liveId, this.title);
        }

        if (this.status === "draft") {
          this.toast("임시저장이 완료되었습니다!", "default");
        } else {
          this.toast("업로드완료! 승인을 기다립니다", "default");
        }
        this.$emit("success"); // 성공 이벤트 emit
        this.releaseActiveFocus();
        this.$emit("close-popup");
        this.renew();
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.toast(`에러가 발생했습니다: ${errorMessage}`, "danger");
        this.renew();
        console.error("Error uploading data:", error);
      } finally {
        this.uploading = false;
      }
    },
    async update() {
      this.releaseActiveFocus();
      // URL 유효성 검사 (임시저장/최종등록 모두 수행)
      if (this.link.trim() !== "") {
        const normalizedLink = this.normalizeUrl(this.link);
        if (!this.checkUrl(normalizedLink)) {
          this.toast("올바른 공연 정보 링크를 입력해주세요", "default");
          return;
        }
      }

      if (!this.validateLinkookTicketCountLimit()) {
        return;
      }

      // 선택 링크들도 정규화
      const normalizedLink = this.link.trim() ? this.normalizeUrl(this.link) : "";
      const normalizedReservationLink = this.reservationLink ? this.normalizeUrl(this.reservationLink) : "";
      const normalizedPosterDetailLink = this.posterDetailLink.trim() ? this.normalizeUrl(this.posterDetailLink) : null;

      const updateData = {
        slug: this.liveData.slug || undefined,
        title: this.title,
        date: this.formatDate(this.selectedDateTime),
        entryTime: this.entryDateTime
          ? this.formatDate(this.entryDateTime)
          : null,
        place: this.place,
        link: normalizedLink,
        reservationLink: normalizedReservationLink,
        // organizer는 변경하지 않음 (권한관리에서 관리)
        linkookReservation: this.reservationType === 'linkook',
        onsiteOnly: this.onsiteOnly || false,
        linkookPrice: this.linkookPrice,
        linkookTicketCount: this.linkookTicketCount,
        linkookPerPersonLimit: this.linkookPerPersonLimit,
        bankName: this.bankName.trim() || null,
        accountNumber: this.accountNumber.trim() || null,
        accountHolder: this.accountHolder.trim() || null,
        // 예매 기간은 공연일시(date)와 동일하게 문자열로 저장 (YYYY.MM.DD HH:mm)
        reservationStartDate: this.normalizeReservationDateString(this.reservationStartDate),
        reservationEndDate: this.normalizeReservationDateString(this.reservationEndDate),
        advancePrice: this.advancePrice,
        doorPrice: this.doorPrice,
        lineup: this.selectedArtists.map(artist => this.getArtistName(artist)),
        crew: this.selectedCrew.map((person) => this.buildCrewPayload(person)),
        crews: this.selectedCrew.map((person) => this.getCrewName(person)).join(", "),
        type: this.type,
        // 이미지 파일명 업데이트 (포스터는 삭제 불가, 새 파일이 있으면 파일명, 삭제되면 빈 문자열, 없으면 기존 값 유지)
        // originalImg는 이미 파일명만 저장되어 있음 (extractFileName으로 추출됨)
        img: this.file1 ? this.file1.name : (this.originalImg || ""),
        banner: this.file2 ? this.file2.name : (this.deletedBanner ? "" : this.originalBanner || ""),
        detail_img: this.detailImageFile ? this.detailImageFile.name : (this.deletedDetailImg ? "" : this.originalDetailImg || ""),
        detail_img2: this.detailImage2File ? this.detailImage2File.name : (this.deletedDetailImg2 ? "" : this.originalDetailImg2 || ""),
        detail_img3: this.detailImage3File ? this.detailImage3File.name : (this.deletedDetailImg3 ? "" : this.originalDetailImg3 || ""),
        detail_img4: this.detailImage4File ? this.detailImage4File.name : (this.deletedDetailImg4 ? "" : this.originalDetailImg4 || ""),
        description: this.description?.trim() || "",
        descriptionHtml: this.descriptionHtml?.trim() || "",
        posterCreatorNickname: this.posterCreatorNickname.trim() || null,
        posterDetailLink: normalizedPosterDetailLink,
        organizerName: this.organizerName.trim() || null,
        organizerContact: this.organizerContact.trim() || null,
        youtubeVideos: this.youtubeVideos.map(v => ({
          vid: v.vid,
          url: v.url,
          title: v.title,
          channel: v.channel,
          channelId: v.channelId || null,
          channelThumbnail: v.channelThumbnail || null,
          channelHandle: v.channelHandle || null,
          videoThumbnail: v.videoThumbnail || null,
          publishedAt: v.publishedAt || null,
        })),
        status: this.status, // 'draft' 또는 'published'
        lastModifiedBy: this.userStore.email,
        lastModifiedByNickname: this.userStore.nickname,
        lastModifiedAt: new Date(),
      };

      try {
        this.uploading = true;

        await updateLive(
          this.liveData.id,
          updateData,
          this.file1,
          this.file2,
          this.detailImageFile,
          this.detailImage2File,
          this.detailImage3File,
          this.detailImage4File
        );

        // 신규 아티스트를 artists 컬렉션에 자동 저장 (아티스트가 있을 때만)
        if (this.selectedArtists.length > 0) {
          const artistNames = this.selectedArtists.map(artist => this.getArtistName(artist));
          await saveNewArtists(artistNames, this.type);
        }

        // 유튜브 영상을 videos 컬렉션에 저장
        if (this.youtubeVideos.length > 0) {
          await this.saveVideosToFirestore(this.liveData.id, this.title);
        }

        if (this.status === "draft") {
          this.toast("임시저장이 완료되었습니다!", "default");
        } else {
          this.toast("수정이 완료되었습니다!", "default");
        }
        this.$emit("success"); // 성공 이벤트 emit
        this.releaseActiveFocus();
        this.$emit("close-popup");
        this.renew();
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.toast(`수정 중 에러가 발생했습니다: ${errorMessage}`, "danger");
        this.renew();
        console.error("Error updating live:", error);
      } finally {
        this.uploading = false;
      }
    },
    async cancelPopup() {
      this.releaseActiveFocus();
      const confirmed = await this.$confirm("작성을 종료하시겠습니까? \n 저장하지 않은 내용은 사라집니다");
      if (confirmed) {
        this.renew();
        this.releaseActiveFocus();
        this.$emit("close-popup");
      }
    },
    // ── 유튜브 영상 등록 ──────────────────────────────────────────
    extractYoutubeId(url) {
      const pattern = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
      const match = url.match(pattern);
      return match ? match[1] : null;
    },
    async addYoutubeVideo() {
      const url = this.youtubeInput.trim();
      if (!url) return;
      const vid = this.extractYoutubeId(url);
      if (!vid) {
        await this.$alert("올바른 YouTube URL을 입력해주세요.");
        return;
      }
      if (this.youtubeVideos.some(v => v.vid === vid)) {
        await this.$alert("이미 추가된 영상입니다.");
        return;
      }
      this.youtubeAdding = true;
      try {
        const info = await this.fetchYoutubeVideoInfo(vid);
        if (!info) return;
        this.youtubeVideos.push({ vid, url, ...info });
        this.youtubeInput = "";
      } finally {
        this.youtubeAdding = false;
      }
    },
    removeYoutubeVideo(index) {
      this.youtubeVideos.splice(index, 1);
    },
    async fetchYoutubeVideoInfo(vid) {
      const apiKey = process.env.VUE_APP_YOUTUBE_API_KEY;
      try {
        const res = await axios.get(
          `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${vid}&key=${apiKey}`
        );
        const items = res.data.items;
        if (!items || items.length === 0) {
          await this.$alert("❌ 영상을 찾을 수 없습니다.");
          return null;
        }
        const snippet = items[0].snippet;
        const title = snippet.title;
        const channel = snippet.channelTitle;
        const channelId = snippet.channelId;
        const videoThumbnail =
          snippet.thumbnails?.maxres?.url ||
          snippet.thumbnails?.standard?.url ||
          snippet.thumbnails?.high?.url ||
          null;
        const publishedAt = snippet.publishedAt || null;

        let channelThumbnail = null;
        let channelHandle = null;
        try {
          const chRes = await axios.get(
            `https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${channelId}&key=${apiKey}`
          );
          const chItems = chRes.data.items;
          if (chItems && chItems.length > 0) {
            const cs = chItems[0].snippet;
            channelThumbnail = cs.thumbnails?.medium?.url || cs.thumbnails?.default?.url || null;
            const customUrl = cs.customUrl || null;
            channelHandle = customUrl
              ? customUrl.startsWith("@") ? customUrl : `@${customUrl}`
              : null;
          }
        } catch (e) {
          console.warn("채널 정보 가져오기 실패:", e);
        }

        return { title, channel, channelId, channelThumbnail, channelHandle, videoThumbnail, publishedAt };
      } catch (err) {
        await this.$alert(`❌ YouTube API 요청 실패: ${err.message}`);
        return null;
      }
    },
    async saveVideosToFirestore(liveId, liveTitle) {
      const lineupNames = this.selectedArtists.map(a => this.getArtistName(a));
      for (const video of this.youtubeVideos) {
        const videoDocRef = doc(firestore, "videos", video.vid);
        await setDoc(
          videoDocRef,
          {
            id: video.vid,
            title: video.title,
            channel: video.channel,
            channelId: video.channelId || null,
            channelThumbnail: video.channelThumbnail || null,
            channelHandle: video.channelHandle || null,
            link: video.url,
            date: video.publishedAt || null,
            registeredAt: serverTimestamp(),
            type: null,
            isCover: false,
            likes: 0,
            lineup: lineupNames,
            liveId: liveId,
            liveTitle: liveTitle,
            registeredBy: this.userStore.email,
            registeredByName: this.userStore.nickname || "",
          },
          { merge: true }
        );
      }
    },
    // ─────────────────────────────────────────────────────────────
    renew() {
      this.releaseActiveFocus();
      this.currentStep = 1; // 단계 초기화
      this.title = "";
      this.place = "";
      this.type = "";
      this.link = "";
      this.reservationLink = "";
      this.organizer = "";
      this.reservationType = ""; // 예매 방식 초기화
      this.onsiteOnly = false; // 현장예매 only 초기화
      this.linkookReservation = false;
      this.linkookPrice = "";
      this.linkookTicketCount = "";
      this.linkookPerPersonLimit = 2;
      this.bankName = "";
      this.accountNumber = "";
      this.accountHolder = "";
      this.reservationStartDate = null;
      this.reservationEndDate = null;
      this.advancePrice = "";
      this.doorPrice = "";
      this.member = "";
      this.posterCreatorNickname = "";
      this.posterDetailLink = "";
      this.organizerName = "";
      this.organizerContact = "";
      this.selectedDateTime = new Date();
      this.entryDateTime = null;
      this.file1 = null;
      this.file2 = null;
      this.detailImageFile = null;
      this.detailImage2File = null;
      this.detailImage3File = null;
      this.detailImage4File = null;
      this.originalImg = null;
      this.originalBanner = null;
      this.originalDetailImg = null;
      this.originalDetailImg2 = null;
      this.originalDetailImg3 = null;
      this.originalDetailImg4 = null;
      this.posterPreviewUrl = null;
      this.bannerPreviewUrl = null;
      this.detailImgPreviewUrl = null;
      this.detailImg2PreviewUrl = null;
      this.detailImg3PreviewUrl = null;
      this.detailImg4PreviewUrl = null;
      this.deletedBanner = false;
      this.deletedDetailImg = false;
      this.deletedDetailImg2 = false;
      this.deletedDetailImg3 = false;
      this.deletedDetailImg4 = false;
      this.selectedArtists = [];
      this.currentArtistInput = "";
      this.selectedArtistData = null;
      this.artistImageLoadPromises = {};
      this.selectedCrew = [];
      this.currentCrewInput = "";
      this.crewInputByRole = {};
      this.crewSelectedDataByRole = {};
      this.filteredCrewByRole = {};
      this.currentCrewRoleInput = "";
      this.filteredCrewRoles = [];
      this.selectedCrewData = null;
      this.crewRoleSections = [];
      this.activeCrewRole = "";
      this.filteredCrew = [];
      this.status = "published";
      this.youtubeVideos = [];
      this.youtubeInput = "";
      this.youtubeAdding = false;
      this.description = "";
      this.descriptionHtml = "";
      this.destroyDescriptionEditor();

      // 파일 입력 필드 초기화
      if (this.$refs.posterFileInput) {
        this.$refs.posterFileInput.value = "";
      }
      if (this.$refs.bannerFileInput) {
        this.$refs.bannerFileInput.value = "";
      }
      if (this.$refs.detailImageFileInput) {
        this.$refs.detailImageFileInput.value = "";
      }
      if (this.$refs.detailImage2FileInput) {
        this.$refs.detailImage2FileInput.value = "";
      }
      if (this.$refs.detailImage3FileInput) {
        this.$refs.detailImage3FileInput.value = "";
      }
      if (this.$refs.detailImage4FileInput) {
        this.$refs.detailImage4FileInput.value = "";
      }
    },
    formatDate(date) {
      if (!(date instanceof Date) || isNaN(date)) {
        return "유효하지 않은 날짜";
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}.${month}.${day} ${hours}:${minutes}`;
    },
    checkUrl(url) {
      const httpsPattern = /^https:\/\/[^\s$.?#].[^\s]*$/i;
      return httpsPattern.test(url);
    },
    // URL에 https://가 없으면 자동으로 붙여주는 함수
    normalizeUrl(url) {
      if (!url || !url.trim()) return url;
      const trimmedUrl = url.trim();
      // 이미 http:// 또는 https://로 시작하면 그대로 반환
      if (/^https?:\/\//i.test(trimmedUrl)) {
        // http://인 경우 https://로 변환
        if (/^http:\/\//i.test(trimmedUrl)) {
          return trimmedUrl.replace(/^http:\/\//i, 'https://');
        }
        return trimmedUrl;
      }
      // 프로토콜이 없으면 https:// 추가
      return `https://${trimmedUrl}`;
    },
    toast(message, type) {
      createToast(message, {
        type: type,
        timeout: 3000,
        position: "top-center",
      });
    },
    getErrorMessage(error) {
      // API 응답 에러 메시지 추출
      if (error.response?.data?.message) {
        return error.response.data.message;
      }
      // API 응답 에러 (다른 형식)
      if (error.response?.data?.error) {
        return error.response.data.error;
      }
      // 일반 에러 메시지
      if (error.message) {
        return error.message;
      }
      // 기타
      return error.toString();
    },
    adjustDropdownWidth() {
      this.$nextTick(() => {
        // 장소 AutoComplete는 appendTo="self"로 입력 옆에 붙어 있어 수동 위치 조정 불필요

        // 아티스트 AutoComplete 드롭다운 조정
        if (this.$refs.artistAutocompleteRef) {
          const autocompleteElement = this.$refs.artistAutocompleteRef.$el;
          const panel = document.querySelector(".artist-autocomplete-panel");

          if (autocompleteElement && panel) {
            const autocompleteWidth = autocompleteElement.offsetWidth;
            const autocompleteRect =
              autocompleteElement.getBoundingClientRect();

            panel.style.width = `${autocompleteWidth}px`;
            panel.style.left = `${autocompleteRect.left}px`;
            panel.style.top = `${autocompleteRect.bottom}px`;
          }
        }
      });
    },
    getStepLabel(step) {
      const labels = {
        1: "기본 정보",
        2: "세부 이미지",
        3: "상세 정보",
        4: "영상 등록",
        5: "예매 방식",
      };
      return labels[step] || "";
    },
    nextStep() {
      // 현재 단계 유효성 검사 (다음 버튼은 선택적)
      if (!this.validateCurrentStep(false)) {
        return;
      }
      if (this.currentStep < 5) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    validateCurrentStep(isSubmit = false) {
      // isSubmit이 true일 때만 엄격한 검사 수행
      // false일 때는 경고만 표시 (임시저장용)

      if (this.currentStep === 1 && isSubmit) {
        if (this.title.trim() === "") {
          this.toast("라이브 제목을 입력해주세요", "default");
          return false;
        }
        if (this.type.trim() === "") {
          this.toast("공연 타입을 선택해주세요", "default");
          return false;
        }
        if (this.place.trim() === "") {
          this.toast("라이브 장소를 입력해주세요", "default");
          return false;
        }
        if (!this.file1 && !this.originalImg) {
          this.toast("포스터를 선택해주세요", "default");
          return false;
        }
      }

      if (this.currentStep === 3 && isSubmit) {
        if (this.link.trim() === "") {
          this.toast("공연 정보 링크를 입력해주세요", "default");
          return false;
        }
        const normalizedLink = this.normalizeUrl(this.link);
        if (!this.checkUrl(normalizedLink)) {
          this.toast("올바른 링크를 입력해주세요", "default");
          return false;
        }
      }

      return true;
    },

  },
};
</script>

<style module>
/* .upload {
  overflow: auto;
} */

/* Step Indicator 스타일 */
.stepIndicator {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  padding: 0 10px;
  gap: 10px;
}

.stepItem {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
}

.stepItem::after {
  content: "";
  position: absolute;
  top: 16px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #e0e0e0;
  z-index: -1;
}

.stepItem:last-child::after {
  display: none;
}

.stepItem.completed::after {
  background: #66bdff;
}

.stepNumber {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f5f5f5;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.3s ease;
}

.stepItem.active .stepNumber {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.stepItem.completed .stepNumber {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.stepLabel {
  font-size: 12px;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
  text-align: center;
  white-space: nowrap;
}

.stepItem.active .stepLabel {
  color: #66bdff;
  font-weight: 600;
}

.stepItem.completed .stepLabel {
  color: #66bdff;
}

.stepDescription {
  background: #f8f9ff;
  border-left: 3px solid #66bdff;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.stepDescription p {
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  font-family: "NanumSquareRoundR", sans-serif;
}

h5 {
  font-family: "Paperlogy5", sans-serif;
  font-style: italic;
  color: #444;
  margin: 5px;
}

h4 {
  font-family: "Paperlogy7", sans-serif;
  font-size: 14px;
  margin: 0 0 8px 0;
  color: #444;
  font-weight: 600;
  position: relative;
  text-align: left;
}

h4 span {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin-left: 4px;
}

.required::after {
  content: "*";
  color: #ff4444;
  font-size: 12px;
  font-weight: bold;
  margin-left: 4px;
  vertical-align: top;
  line-height: 1;
}

input {
  height: 40px;
  width: 100%;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
}

input[type="radio" i] {
  display: none;
}

input[type="checkbox"] {
  width: 12px;
  height: 12px !important;
  cursor: pointer;
  accent-color: #66bdff;
  margin: 0;
}

.radio {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.radio input[type="radio"] {
  display: none;
}

.radio label {
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
  font-weight: 400;
  position: relative;
  display: block;
  flex: 1;
  min-width: 80px;
}

.radio label:hover {
  border-color: #66bdff;
  color: #66bdff;
}

.radio label:has(input[type="radio"]:checked) {
  border-color: #66bdff;
  background: #66bdff;
  color: #fff;
  font-weight: 500;
}

.quantityButtonGroup {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.quantityBtn {
  flex: 1;
  min-width: 0;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  color: #666;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.quantityBtn:hover:not(:disabled) {
  border-color: #66bdff;
  color: #fff;
}

.quantityBtn.selected {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.quantityBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

label {
  padding-right: 0;
}

.checkboxLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
}

.checkbox {
  margin: 0;
}

.picker {
  width: 100%;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.textInput {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

.textInput::placeholder {
  color: #999;
}

.textInput:focus {
  outline: none;
  border-color: #66bdff;
}

.textInput:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.fileInputLabel {
  display: block;
  cursor: pointer;
}

.fileInputHidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}

.fileInputButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 20px;
  border: 1px solid #66bdff;
  border-radius: 6px;
  background: #f8f9ff;
  color: #66bdff;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.15s ease;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}

.fileInputButton:hover {
  border-color: #4a9eff;
  background: #f0f7ff;
  color: #4a9eff;
}

.fileInputButton span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.fileInputLabel:has(.fileInputHidden:disabled) .fileInputButton {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
}

.fileInputLabel:has(.fileInputHidden:disabled) .fileInputButton:hover {
  border-color: #ddd;
  background: #f5f5f5;
  color: #333;
}

.inputs {
  width: 95%;
  margin: auto;
}

.input {
  padding: 0;
  margin-bottom: 20px;
}

.descriptionEditorHost {
  width: 100%;
  text-align: left;
}

.tinyEditorHost {
  width: 100%;
  text-align: left;
}

.tinyEditorHost :global(.tox-tinymce) {
  border: 1px solid #ddd;
  border-radius: 6px;
}

/* 모달(z-index 9999) 위로 TinyMCE 메뉴/드롭다운/대화상자가 올라오도록 */
:global(.tox.tox-tinymce-aux),
:global(.tox-silver-sink) {
  z-index: 100000 !important;
}

:global(.tox-dialog-wrap) {
  z-index: 100001 !important;
}

/* overflow:hidden 이면 이미지/링크 팝업(absolute, defaultUI 자식)이 잘림 */
.descriptionEditorHost :global(.toastui-editor-defaultUI) {
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: visible;
}

/* 이미지 첨부 팝업: 하단 버튼을 가리지 않도록 화면 중앙에 띄움(크기는 기본값) */
.descriptionEditorHost :global(.toastui-editor-popup.toastui-editor-popup-add-image) {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  z-index: 2000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

/* Toast UI: 툴바·아이콘을 모달에 맞게 축소 */
.descriptionEditorHost :global(.toastui-editor-defaultUI-toolbar) {
  height: 36px;
  padding: 0 8px;
  align-items: center;
}

.descriptionEditorHost :global(.toastui-editor-toolbar) {
  height: 36px;
  box-sizing: border-box;
}

.descriptionEditorHost :global(.toastui-editor-defaultUI-toolbar button) {
  width: 26px;
  height: 26px;
  margin: 4px 2px;
  transform: scale(0.78);
  transform-origin: center center;
}

.descriptionEditorHost :global(.toastui-editor-toolbar-divider) {
  height: 14px;
  margin: 10px 6px;
}

.descriptionEditorHost :global(.toastui-editor-toolbar-item-wrapper) {
  height: 26px;
  line-height: 26px;
}

.logo {
  width: 70%;
}

.buttons {
  margin-top: 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.cancel {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cancel:hover:not(:disabled) {
  background: #e8e8e8;
  border-color: #ccc;
}

.cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.saveDraft {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.saveDraft:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
}

.saveDraft:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prevButton {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.prevButton:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
}

.prevButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nextButton {
  padding: 12px 24px;
  border-radius: 6px;
  background: #66bdff;
  color: #fff;
  border: none;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.nextButton:hover:not(:disabled) {
  background: #4a9eff;
}

.nextButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.register {
  padding: 12px 24px;
  border-radius: 6px;
  background: #66bdff;
  color: #fff;
  border: none;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.register:hover:not(:disabled) {
  background: #4a9eff;
}

.register:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  box-sizing: border-box;
  vertical-align: middle;
}

/* AutoComplete 스타일링 개선 */
.autocomplete {
  width: 100%;
  font-size: 14px;
  position: relative;
}


.artistList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  margin-top: 12px;
}

.artistItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: background 0.2s;
}

.artistItem:hover {
  background: #f0f1f3;
}

.artistInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.profileImage {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.profilePlaceholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
}

.artistDetails {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  text-align: left;
}

.artistName {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.artistNameRow {
  display: flex;
  align-items: center;
  gap: 6px;
}

.artistType {
  font-size: 12px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.newBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 11px;
  color: #fff;
  line-height: 12px;
  background: #66bdff;
  font-family: "NanumSquareRoundB", sans-serif;
}

.removeButton {
  padding: 6px 12px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-left: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.removeButton:hover:not(:disabled) {
  background: #d32f2f;
}

.removeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.addArtistForm {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  position: relative;
}

.addArtistForm .artistSearchAutocomplete {
  flex: 1;
  width: 100%;
  position: relative;
}

.crewRoleSelector {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.crewRoleAutocomplete {
  flex: 1;
  width: 100%;
}

.crewRoleSelector :global(.p-autocomplete) {
  width: 100%;
}

.crewRoleSelector :global(.p-autocomplete-input) {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.crewRoleSelector :global(.p-autocomplete-dropdown) {
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 8px 8px 0;
}

.crewRoleSelector :global(.p-autocomplete-input:focus) {
  outline: none;
  border-color: #2196f3;
  box-shadow: none;
}

.crewRoleSelector :global(.p-autocomplete:focus-within .p-autocomplete-dropdown) {
  border-color: #2196f3;
}

.addRoleButton {
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: #4caf50;
  color: #fff;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  white-space: nowrap;
}

.addRoleButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.crewDepthBlock {
  margin-left: 8px;
  padding-left: 12px;
  border-left: 2px solid #eceff3;
  margin-bottom: 8px;
}

.crewRoleTab {
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #555;
  font-size: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
}

.activeCrewRoleTab {
  background: #f5f7fa;
  border-color: #cfd8e3;
  color: #222;
}

.crewSectionList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.crewAddForm {
  margin-top: 4px;
}

.crewSection {
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 8px;
  background: #fff;
}

.crewSectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.removeRoleButton {
  padding: 3px 8px;
  border: 1px solid #eadede;
  border-radius: 6px;
  background: #fff;
  color: #8a5d5d;
  font-size: 11px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
}

.removeRoleButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


.addArtistForm :global(.p-autocomplete-input) {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 14px;
  transition: all 0.2s;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.addArtistForm :global(.p-autocomplete-input:focus) {
  outline: none;
  border-color: #2196f3;
  box-shadow: none;
}

.addArtistForm :global(.p-autocomplete-input:disabled) {
  background: #f5f5f5;
  cursor: not-allowed;
}

.addArtistForm :global(.p-autocomplete-dropdown) {
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  transition: all 0.2s;
}

.addArtistForm :global(.p-autocomplete-dropdown:hover) {
  background: #e9ecef;
}

.addArtistForm :global(.p-autocomplete:focus-within .p-autocomplete-dropdown) {
  border-color: #2196f3;
}

.addButton {
  padding: 10px 20px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
}

.addButton:hover:not(:disabled) {
  background: #1976d2;
}

.addButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}



@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.imagePreviewContainer {
  margin-bottom: 12px;
}

.imagePreview {
  position: relative;
  display: inline-block;
  max-width: 250px;
  max-height: 350px;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  background: #fafafa;
}

.previewImage {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.deleteImageButton {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #ff4444;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.15s ease;
}

.deleteImageButton:hover {
  background: #e03333;
}

.deleteImageButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.newFileInfo {
  margin-top: 6px;
  font-size: 12px;
  color: #66bdff;
  font-family: "NanumSquareRoundR", sans-serif;
}

.helpText {
  font-size: 12px;
  color: #999;
  margin: 6px 0 0 0;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.5;
}

.infoText {
  font-size: 12px;
  color: #66bdff;
  margin: 8px 0;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.6;
  padding: 8px 12px;
  background: #f0f7ff;
  border-radius: 4px;
  border-left: 3px solid #66bdff;
}

:global(.dp__menu) {
  --dp-font-size: 0.875rem;
  --dp-cell-size: 32px;
  --dp-menu-min-width: 240px;
  --dp-menu-padding: 6px 8px;
  --dp-calendar-header-cell-padding: 6px;
  --dp-row-margin: 2px 0;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .stepIndicator {
    margin-bottom: 24px;
    padding: 0 5px;
    gap: 5px;
  }

  .stepNumber {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .stepLabel {
    font-size: 10px;
  }

  .stepItem::after {
    top: 14px;
  }

  .stepDescription {
    padding: 10px 12px;
    margin-bottom: 16px;
  }

  .stepDescription p {
    font-size: 12px;
  }

  h4 {
    font-size: 13px;
    margin: 0 0 6px 0;
  }

  h4 span {
    font-size: 11px !important;
  }

  input {
    font-size: 14px;
    height: 38px !important;
  }

  .radio {
    gap: 6px;
  }

  .radio label {
    font-size: 13px;
    padding: 8px 16px;
    min-width: 70px;
  }

  .picker {
    height: 38px;
  }

  .textInput {
    padding: 8px 12px;
  }

  .input {
    margin-bottom: 18px;
  }

  .buttons {
    margin-top: 20px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .cancel {
    width: 100%;
    padding: 12px;
    font-size: 14px;
    order: 4;
  }

  .saveDraft {
    width: 100%;
    padding: 12px;
    font-size: 14px;
    order: 3;
  }

  .prevButton {
    flex: 1;
    padding: 12px;
    font-size: 14px;
    order: 1;
  }

  .nextButton,
  .register {
    flex: 1;
    padding: 12px;
    font-size: 14px;
    order: 2;
  }

  .checkboxLabel {
    font-size: 13px;
  }

  .spinner {
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }

  .placeAutocomplete :global(.p-autocomplete-input) {
    padding: 8px 12px;
    height: 38px;
    border-radius: 6px 0 0 6px;
  }

  .placeAutocomplete :global(.p-autocomplete-dropdown) {
    height: 38px;
    width: 38px;
    min-height: 38px;
    border-radius: 0 6px 6px 0;
  }

  :global(.place-autocomplete-panel) {
    max-height: 180px;
  }

  :global(.place-autocomplete-panel .p-autocomplete-item) {
    padding: 8px 12px;
    font-size: 13px;
  }

  .artistList {
    gap: 6px;
    margin-bottom: 12px;
  }

  .artistItem {
    padding: 10px 12px;
  }

  .profileImage {
    width: 36px;
    height: 36px;
  }

  .profilePlaceholder {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .artistName {
    font-size: 14px;
  }

  .removeButton {
    padding: 5px 10px;
    font-size: 12px;
  }

  .addArtistForm {
    flex-direction: column;
    gap: 6px;
  }

  .crewRoleSelector {
    flex-direction: column;
    align-items: stretch;
  }

  .addRoleButton {
    width: 100%;
  }

  .addButton {
    padding: 10px 16px;
  }

  .imagePreview {
    max-width: 200px;
    max-height: 280px;
  }

  .deleteImageButton {
    top: 4px;
    right: 4px;
    padding: 5px 10px;
    font-size: 11px;
  }

  .newFileInfo {
    font-size: 11px;
  }

  .infoText {
    font-size: 11px;
    padding: 6px 10px;
  }

  .fileInputButton {
    padding: 10px 16px;
    font-size: 13px;
    width: 100%;
  }

  .fileInputButton span {
    max-width: 100%;
  }
}

/* AutoComplete 드롭다운 패널 스타일 */
:global(.artist-autocomplete-panel) {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  width: 100% !important;
  max-width: none !important;
  z-index: 1100 !important;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
  margin-top: 4px !important;
}

:global(.artist-autocomplete-panel .p-autocomplete-items) {
  padding: 4px 0;
}

:global(.artist-autocomplete-panel .p-autocomplete-item) {
  min-height: 44px;
  padding: 6px 12px !important;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
}

:global(.artist-autocomplete-panel .p-autocomplete-item:hover) {
  background: #f5f5f5;
}

:global(.artist-autocomplete-panel .p-autocomplete-item.p-highlight) {
  background: #e3f2fd;
}

/* 아티스트 옵션 스타일 */
:global(.artist-option) {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 0;
  justify-content: flex-start;
  text-align: left;
}

:global(.option-profile-image) {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

:global(.option-profile-placeholder) {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  flex-shrink: 0;
}

:global(.option-details) {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  text-align: left;
}

:global(.option-name) {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

:global(.option-type) {
  font-size: 12px;
  color: #666;
}

/* 유튜브 영상 등록 */
.youtubeInputRow {
  display: flex;
  gap: 8px;
  align-items: center;
}

.youtubeInputRow .textInput {
  flex: 1;
  min-width: 0;
}

.youtubeVideoList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}

.youtubeVideoItem {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px;
}

.youtubeVideoThumb {
  width: 100px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: #ddd;
}

.youtubeVideoInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.youtubeVideoTitle {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.youtubeVideoChannel {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #868e96;
}

@media (max-width: 480px) {
  .youtubeInputRow {
    flex-wrap: nowrap;
  }

  .youtubeVideoItem {
    gap: 8px;
    padding: 8px;
  }

  .youtubeVideoThumb {
    width: 80px;
    height: 45px;
  }

  .youtubeVideoTitle {
    font-size: 12px;
  }

  .youtubeVideoChannel {
    font-size: 11px;
  }
}
</style>
